import React, { createContext, useState, useEffect, ReactNode, useContext, useCallback, useRef, useMemo } from 'react'
import {
	clusterApiUrl,
	Connection,
	PublicKey,
	Keypair,
	LAMPORTS_PER_SOL,
	Transaction,
	SystemProgram,
	sendAndConfirmTransaction,
	TransactionMessage,
	ComputeBudgetProgram,
} from '@solana/web3.js';

import { 
	getAssociatedTokenAddress, 
	createAssociatedTokenAccountInstruction,
	getMint,
	getAccount,
	TOKEN_PROGRAM_ID,
	createTransferCheckedInstruction,
	ASSOCIATED_TOKEN_PROGRAM_ID,
	getTokenMetadata
} from '@solana/spl-token';


// import { fetchDigitalAsset, mplTokenMetadata } from '@metaplex-foundation/mpl-token-metadata'
// import { createUmi } from "@metaplex-foundation/umi-bundle-defaults";
// import { generateSigner, percentAmount } from '@metaplex-foundation/umi'

import bs58 from 'bs58';
import BN from 'bn.js';
import axios from 'axios'
import i18next from 'i18next'
import moment from 'moment'

// WASM 모듈 import
import { initializeWasm } from '../wasm/wasmutils';
import { 
	fetch_transaction_data, 
	add_server_tx_history, 
	get_server_tx_id_list, 
	get_server_tx_history 
} from '../wasm/rust_wassm_fetch';

// types
import { SYNC_STATUS, SyncTx } from './types/NetworkTypes';

import { sleep } from '../utils/utils';
import { DefaultTokenContext } from './DefaultTokenContext';
import useSecureStorage from '../hooks/useSecureStorage';
import { getTelegramId } from '../utils/telegramClientData';
import { useTranslation } from 'react-i18next';
import { useInterval } from 'react-interval-hook';
import { tgShowAlert } from '../utils/telegramPopup';

// Metaplex Token Metadata Program
const METADATA_PROGRAM_ID = new PublicKey('metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s');

interface SolanaContextProps {
	networkName: string,
	balance: number,
	countSyncProcess: number,
	countSyncComplete: number,
	syncStatus: string,
	lastSyncTxs: SyncTx[],
	reqTxStop: boolean,
	startDbSyncTimer: () => void,
	stopDbSyncTimer: () => void,
	getBalance: (walletAddress: string, isFormatting?: boolean, updateType?: string) => Promise<number>
	getBalanceFromServer: (walletAddress: string, coinName: string) => Promise<number>
	getTokenBalance: (walletAddress: string, mintAddress: string) => Promise<string>
	getTokenBalanceFromServer: (
		walletAddress: string, 
		mintAddress: string, 
		coinName: string
	) => Promise<number>
	estimateGas: (walletAddress: string) => Promise<string>
	sendTransactionWithPrivateKey: (
		privateKey: string, 
		toAddress: string, 
		amount: string, 
		tokenSymbol: string, 
		tokenAddress: string
	) => Promise<string>
	getTotalTxCount: (walletAddress: string) => Promise<any>
	stopRequestTxData: (isStop?:boolean) => void
	syncAllTransaction: (walletAddress: string) => Promise<any>
	getTokenInfo: (mintAddress: string) => Promise<any>
	getServerTxHistory: (walletAddress: string, network: string, tid: string, tokenName: string, page: number, take: number) => Promise<any>
	getBlockScanUrl: (txid: string) => Promise<string>
	isValidAddress: (address: string) => Promise<Boolean>
}


const defaultContext: SolanaContextProps = {
	networkName: '',
	balance: 0,
	countSyncProcess: 0,
	countSyncComplete: 0,
	syncStatus: SYNC_STATUS.CHECKING,
	lastSyncTxs: [],
	reqTxStop: false,
	startDbSyncTimer: async () => {},
	stopDbSyncTimer: async () => {},
	getBalance: async (walletAddress: string, isFormatting:boolean = true, updateType:string='await') => Promise.resolve(0),
	getBalanceFromServer: async (walletAddress: string, coinName: string) => Promise.resolve(0),
	getTokenBalance: async (walletAddress: string, mintAddress: string) => Promise.resolve('0.0'),
	getTokenBalanceFromServer: async (walletAddress: string, mintAddress: string, coinName: string) => Promise.resolve(0),
	estimateGas: async (walletAddress: string) => Promise.resolve('0'),
	sendTransactionWithPrivateKey: async (privateKey: string, toAddress: string, amount: string, tokenSymbol: string, tokenAddress: string) => Promise.resolve(''),
	getTotalTxCount: async (walletAddress: string) => Promise.resolve(0),
	stopRequestTxData: (isStop?:boolean) => {},
	syncAllTransaction: async (walletAddress: string) => Promise.resolve([]),
	getTokenInfo: async (mintAddress: string) => Promise.resolve(null),
	getServerTxHistory: async (walletAddress: string, network: string, tid: string, tokenName: string, page: number, take: number) => Promise.resolve([]),
	getBlockScanUrl: async (txid: string) => Promise.resolve(''),
	isValidAddress: async (address: string) => Promise.resolve(false)
}

export const SolanaContext = createContext<SolanaContextProps>(defaultContext)

const cacheExpiryTime = 10000;
const gasPriceCacheExpiryTime = 10000;
const reqDelay = 800;
const dbTxInterval = 60000;
const reqdbTxSyncInterval = 20000; // db tx 현행화 요청 최소 간격(20초)

export const SolanaProvider = ({ children }: { children: ReactNode }) => {
	const { t } = useTranslation();
	
	const [networkName, setNetworkName] = useState('solana')
	const [balance, setBalance] = useState(0)
	const [countSyncProcess, setCountSyncProcess] = useState(0) // DB 현행화 진행
	const [countSyncComplete, setCountSyncComplete] = useState(0) // DB 현행화 수량
	const [syncStatus, setSyncStatus] = useState(SYNC_STATUS.COMPLETE) // DB 현행화 진행 상태
	const [lastSyncTxs, setLastSyncTxs] = useState<SyncTx[]>([])
	const [wasmInitialized, setWasmInitialized] = useState(false);

	const [isRunLimitTransactionHistory, setIsRunLimitTransactionHistory] = useState(false)
	const runLimitTransactionHistoryRef = useRef(isRunLimitTransactionHistory); // ref reqTxStop 참조
	const [isRunAllTransactionHistory, setIsRunAllTransactionHistory] = useState(false)
	const runAllTransactionHistoryRef = useRef(isRunAllTransactionHistory); // ref reqTxStop 참조
	const [reqTxStop, setReqTxStop] = useState(false) // req tx stop flag
	const reqTxStopRef = useRef(reqTxStop); // ref reqTxStop 참조
	const [syncKey, setSyncKey] = useState('')
	const syncKeyRef = useRef(syncKey); // ref reqTxStop 참조

	const [cache, setCache] = useState<any>({});
	const [reqDbTxInterval, setReqDbTxInterval] = useState(0);
	const reqDbTxIntervalRef = useRef(reqDbTxInterval); // ref reqTxStop 참조

	const [tokenBalanceCache, setTokenBalanceCache] = useState<any>({});
	const [gasPriceCache, setGasPriceCache] = useState<any>({});
	

	// const { searchTokenContract, searchNetwork } = useContext(DefaultTokenContext)
	const { getBlockChain, getWalletAddress } = useSecureStorage()
	
	const defaultTokenContext = useContext(DefaultTokenContext);
	const solNetwork = defaultTokenContext.searchNetwork('solana');

	const connection = useMemo(() => {
		console.log(`defaultTokenContext searchNetwork Init : solana `, solNetwork)

		// DefaultTokenContext에서 'solana' 네트워크 정보를 가져옴
		if (!solNetwork) {
			console.warn(`SolanaContext connection solNetwork is null`)
			return null;
		}

		// 네트워크 정보가 없으면 fallback으로 env 값을 사용
		const rpcUrl = solNetwork?.rpc_http || '';
		const wsUrl = solNetwork?.rpc_ws || '';

		return new Connection(rpcUrl, {
			commitment: 'confirmed',
			wsEndpoint: wsUrl,
		});
	}, [solNetwork]);

	useEffect(() => {
		if (wasmInitialized) return; // 이미 초기화된 경우 재실행 막기

		const initNetwork = async () => {
			try {
				// 웹어셈블리 초기화
				console.warn(`SolanaContext 웹 어셈블리 init`)
				// await init();
				await initializeWasm();

				setWasmInitialized(true);
			} catch(error) {
				console.error(`SolanaContext 웹 어셈블리 init error : `, error)
			}
		}
		
    initNetwork()
	}, [wasmInitialized])

	// DB Tx 현행화 타이머
	const syncDbTxTimer = async () => {
		const walletAddress = await getWalletAddress()
		syncAllTransaction(walletAddress).then(() => {
			stop(); //timer 종료
		})
		.catch((error) => {
			console.error(`Error syncDbTxTimer syncAllTransaction : `, error)
			stop(); //timer 종료
		})
	}
	
	const { start, stop, isActive } = useInterval(
        syncDbTxTimer,
        dbTxInterval,
        {
            autoStart: false,
            immediate: true, // start 후 즉시 타이머 실행
            selfCorrecting: true,
            onFinish: () => {
                console.log('useInterval Callback when timer is stopped');
				stop()
            },
        }
    );

	 // 상태가 변경될 때마다 useRef를 업데이트
	useEffect(() => {
		reqTxStopRef.current = reqTxStop;
	}, [reqTxStop]);

	// 상태가 변경될 때마다 useRef를 업데이트
	useEffect(() => {
		runAllTransactionHistoryRef.current = isRunAllTransactionHistory;
	}, [isRunAllTransactionHistory]);

	// 상태가 변경될 때마다 useRef를 업데이트
	useEffect(() => {
		runLimitTransactionHistoryRef.current = isRunLimitTransactionHistory;
	}, [isRunLimitTransactionHistory]);

	// 상태가 변경될 때마다 useRef를 업데이트
	useEffect(() => {
		syncKeyRef.current = syncKey;
	}, [syncKey]);
	
	// 상태가 변경될 때마다 useRef를 업데이트
	useEffect(() => {
		reqDbTxIntervalRef.current = reqDbTxInterval;
	}, [reqDbTxInterval]);

	/**
	 * DB TX 현행화 타이머 시작
	 */
	const startDbSyncTimer = async () => {
		// 이전 요청으로부터 20초가 지나지 않았다면 리턴
		const now = Date.now();
		const lastRequestTime = reqDbTxIntervalRef.current;

		if ((now - lastRequestTime) < reqdbTxSyncInterval) { // 20초(20000ms) 체크
			console.warn(`DB TX 현행화 마지막 실행시간 20초 지나지 않음 : `, (now - lastRequestTime))
			await tgShowAlert(t('balance_screen_message_14'))
			return;
		}
		
		// 현재 시간을 마지막 요청 시간으로 저장
		setReqDbTxInterval(now);

		console.log(`defaultTokenContext startDbSyncTimer connection :  `, connection)
		
		const walletAddress = await getWalletAddress()
		await syncAllTransaction(walletAddress);
	}

	/**
	 * DB TX 현행화 타이머 종료
	 */
	const stopDbSyncTimer = async () => {
		console.log(`DB TX 현행화 스케줄러 종료`)
		stop();
	}

	/**
	 * 코인 RPC 잔액 조회
	 * @param walletAddress 
	 * @param isFormatting : 잔액 포맷 여부
	 * @returns 
	 */
	const getBalance = async (walletAddress: string, isFormatting:boolean = true, updateType:string='await'): Promise<number> => {
		try {
			const cacheKey = 'coinbalance_'+solNetwork?.symbol.toLowerCase();
			
			let cachedData = 0;
			if('await' === updateType) {
                const balance = await getBalanceFromRpc(walletAddress);
                localStorage.setItem(cacheKey, balance.toString());
                cachedData = balance;
            } else if('promise' === updateType) {
                getBalanceFromRpc(walletAddress)
                .then((response) => {
                    // 코인 수량 원본 저장
                    const balance = response.toString() ? response.toString() : '0';
                    localStorage.setItem(cacheKey, balance.toString());
                })
                .catch((error) => {})
                
                const temp = localStorage.getItem(cacheKey);
                cachedData = Number(temp?.toString() ?? '0')
            } else if('cache' === updateType) {
                const temp = localStorage.getItem(cacheKey);
                cachedData = Number(temp?.toString() ?? '0');
            } else {
                const temp = localStorage.getItem(cacheKey);
                cachedData = Number(temp?.toString() ?? '0');
            }
			
			
			let formattedBalance = 0
			if(true === isFormatting) {
				formattedBalance = cachedData / LAMPORTS_PER_SOL
				console.log(`SOL 코인 보유수량 Formatting : ${formattedBalance}`)
				
				setCache(cache)
				setBalance(balance);
			} else {
				formattedBalance = cachedData
				console.log(`SOL 코인 보유수량 : ${formattedBalance}`)
			}

			return formattedBalance;
		} catch (error) {
			console.error(`${walletAddress}의 잔액 조회 중 오류 발생:`, error);
			alert(t('rpc_req_message_01'))
			return 0;
		}
	}

	/**
	 * 코인 RPC 잔액 조회
	 * @param walletAddress 
	 * @returns 
	 */
	const getBalanceFromRpc = async (walletAddress: string): Promise<number> => {
		try {
			if (!connection) {
				console.error('Connection is not initialized');
				return 0;
			}

			const publicKey = new PublicKey(walletAddress);
			const balance = await connection.getBalance(publicKey);
			
			let formattedBalance = balance
			console.log(`SOL 코인 보유수량 : ${formattedBalance}`)

			return formattedBalance;
		} catch (error) {
			console.error(`${walletAddress}의 잔액 조회 중 오류 발생:`, error);
			alert(t('rpc_req_message_01'))
			return 0;
		}
	}



	/**
	 * 코인 서버 잔액 조회
	 * @param walletAddress 
	 * @returns 
	 */
	const getBalanceFromServer = async (walletAddress: string, coinName: string, isFormatting:boolean = true): Promise<number> => {
		
		// 파라미터 유효성 검사
		if (!walletAddress || typeof walletAddress !== 'string' || walletAddress.trim() === '') {
			console.error('유효하지 않은 지갑 주소입니다.');
			return 0;
		}

		if (!coinName || typeof coinName !== 'string' || coinName.trim() === '') {
			console.error('유효하지 않은 코인명입니다.');
			return 0;
		}
		
		const network = await getBlockChain()
		const telegramId = await getTelegramId()

		if('' === network) {
			console.warn(`getBalanceFromServer 잘못된 네트워크 정보`)
			// await tgShowAlert(t('balance_screen_message_05'))
			return 0;
		}
		if('' === telegramId) {
			console.warn(`getBalanceFromServer 잘못된 텔레그램 유저 정보`)
			// await tgShowAlert(t('balance_screen_message_10'))
			return 0;
		}

		try {
			const response = await axios.post(`${process.env.REACT_APP_QWALLET_SERVER_URL}/v1/tx/balance`, {
				wallet_address: walletAddress,
				network: network,
				coin_name: coinName,
				tid: telegramId
			});

			console.log(`서버에서 받은 잔액 데이터:`, response.data);
			
			if (response.data && response.data.result === true) {
				let formattedBalance = 0;
				if(response.data.data.ta_total_amount) {
					if(true === isFormatting) {
						formattedBalance = response.data.data.ta_total_amount / LAMPORTS_PER_SOL;
					} else {
						formattedBalance = response.data.data.ta_total_amount;
					}
					
				}
				console.log(`SOL 코인 보유수량 : ${formattedBalance}`)
				return formattedBalance;
			}
			
			return 0;

		} catch (error) {
			console.error('서버에서 잔액 조회 중 오류 발생:', error);
			alert('An issue occurred while fetching the sol coin balance from server')
			return 0;
		}
	}

	/**
	 * SPL 토큰 RPC 잔액 조회
	 * @param walletAddress 
	 * @param mintAddress 
	 * @param isFormatting : 잔액 포맷 여부
	 * 
	 * @returns 
	 */
	const getTokenBalance = 
	async (
		walletAddress: string, 
		mintAddress: string, 
		isFormatting:boolean = true, 
		updateType:string='await'
	): Promise<string> => {
		try {
			// const cacheKey = 'tokenbalance_'+process.env.REACT_APP_SOLANA_NETWORK_NAME+'_'+mintAddress;
			const cacheKey = 'tokenbalance_'+solNetwork?.symbol.toLowerCase()+'_'+mintAddress;

			let cachedData;

			if('await' === updateType) {
                const temp = await getTokenBalanceFromRpc(walletAddress, mintAddress);
                const balance = temp.tokenBalance.toString() ? temp.tokenBalance.toString() : '0';
                const decimals = temp.decimals.toString() ? temp.decimals.toString() : '0';
                cachedData = {tokenBalance:balance, decimals:decimals};
                localStorage.setItem(cacheKey, JSON.stringify(cachedData));

            } else if('promise' === updateType) {
                getTokenBalanceFromRpc(walletAddress, mintAddress)
                .then((response) => {
                    // 토큰 수량 원본 저장
                    const balance = response.tokenBalance.toString() ? response.tokenBalance.toString() : '0';
                    const decimals = response.decimals.toString() ? response.decimals.toString() : '0';
                    localStorage.setItem(cacheKey, JSON.stringify({tokenBalance:balance, decimals:decimals}));
                })
                .catch((error) => {})
                
                const temp = localStorage.getItem(cacheKey);
                cachedData = JSON.parse(temp ?? '{"tokenBalance":0,"decimals":0}');
            } else if('cache' === updateType) {
                const temp = localStorage.getItem(cacheKey);
                cachedData = JSON.parse(temp ?? '{"tokenBalance":0,"decimals":0}');
            } else {
                const temp = localStorage.getItem(cacheKey);
                cachedData = JSON.parse(temp ?? '{"tokenBalance":0,"decimals":0}');
            }

			// 보유량 계산
			let formattedBalance = 0
			if(true === isFormatting) {
				formattedBalance = Number(cachedData.tokenBalance) / Math.pow(10, cachedData.decimals)
			} else {
				formattedBalance = Number(cachedData.tokenBalance)
			}
			
			console.log(`SPL 토큰 보유수량 : ${formattedBalance}`)

			setTokenBalanceCache(tokenBalanceCache)

			return formattedBalance.toString()
		} catch (error) {
			console.error('Error getting spl token balance:', error)
			// alert('An issue occurred while fetching the spl token balance from rpc server')
			return '0'
		}
}

	const getTokenBalanceFromRpc = 
	async (
		walletAddress: string, 
		mintAddress: string
	): Promise<{tokenBalance:number, decimals:number}> => {
		try {
			const cacheKey = 'tokenbalance_'+solNetwork?.symbol.toLowerCase()+'_'+mintAddress;

			if (!connection) {
				console.error('Connection is not initialized');
				return {tokenBalance:0, decimals:0};
			}

			const walletPublicKey = new PublicKey(walletAddress);
			const mintPublicKey = new PublicKey(mintAddress);
			
			// 지갑 소유자의 모든 토큰 계정 조회
			const tokenAccounts = await connection.getTokenAccountsByOwner(walletPublicKey, {
				mint: mintPublicKey,
			});

			if (tokenAccounts.value.length === 0) {
				console.warn('해당 지갑에 지정된 토큰이 없습니다.', mintAddress);
				return {tokenBalance:0, decimals:0};
			}

			// 첫 번째 토큰 계정 선택
			const tokenAccountPubkey = tokenAccounts.value[0].pubkey;

			// 토큰 계정 정보 조회
			const tokenAccount = await getAccount(connection, tokenAccountPubkey);
		
			// 민트 정보 조회
			const mintInfo = await getMint(connection, mintPublicKey);

			// 보유량 계산
			// let formattedBalance = 0
			// if(true === isFormatting) {
			// 	formattedBalance = Number(tokenAccount.amount) / Math.pow(10, mintInfo.decimals)
			// } else {
			// 	formattedBalance = Number(tokenAccount.amount)
			// }
			
			let formattedBalance = Number(tokenAccount.amount)
			console.log(`SPL 토큰 보유수량 : ${formattedBalance}`)

			setTokenBalanceCache(tokenBalanceCache)

			// return formattedBalance.toString()
			return {tokenBalance: formattedBalance, decimals: mintInfo.decimals};
		} catch (error) {
			console.error('Error getting spl token balance:', error)
			// alert('An issue occurred while fetching the spl token balance from rpc server')
			return {tokenBalance:0, decimals:0};
		}
	}


	/**
	 * SPL 토큰 서버 잔액 조회
	 * @param walletAddress 
	 * @param mintAddress 
	 * @param isFormatting : 잔액 포맷 여부
	 * 
	 * @returns 
	 */
	const getTokenBalanceFromServer = 
	async (
		walletAddress: string, 
		mintAddress: string, 
		coinName: string, 
		isFormatting:boolean = true
	): Promise<number> => {
		
		// 파라미터 유효성 검사
		if (!walletAddress || typeof walletAddress !== 'string' || walletAddress.trim() === '') {
			console.error('유효하지 않은 지갑 주소입니다.');
			return 0;
		}

		if (!coinName || typeof coinName !== 'string' || coinName.trim() === '') {
			console.error('유효하지 않은 코인명입니다.');
			return 0;
		}

		if (!mintAddress || typeof mintAddress !== 'string' || mintAddress.trim() === '') {
			console.error('유효하지 않은 토큰 주소 입니다.');
			return 0;
		}

		if (!connection) {
			console.error('Connection is not initialized');
			return 0;
		}
		
		const network = await getBlockChain()
		const telegramId = await getTelegramId()

		try {
			const response = await axios.post(`${process.env.REACT_APP_QWALLET_SERVER_URL}/v1/tx/balance`, {
				wallet_address: walletAddress,
				network: network,
				coin_name: coinName,
				tid: telegramId
			});

			console.log(`서버에서 받은 잔액 데이터:`, response.data);
			
			if (response.data && response.data.result === true) {
				const walletPublicKey = new PublicKey(walletAddress);
				const mintPublicKey = new PublicKey(mintAddress);
				
				// 지갑 소유자의 모든 토큰 계정 조회
				const tokenAccounts = await connection.getTokenAccountsByOwner(walletPublicKey, {mint: mintPublicKey});

				if (tokenAccounts.value.length === 0) {
					console.warn('해당 지갑에 지정된 토큰이 없습니다.', mintAddress);
					return 0;
				}
			
				// 민트 정보 조회
				const mintInfo = await getMint(connection, mintPublicKey);

				// 보유량 계산
				let formattedBalance = 0
				if(response.data.data.ta_total_amount) {
					if(true === isFormatting) {
						formattedBalance = Number(response.data.data.ta_total_amount) / Math.pow(10, mintInfo.decimals)
					} else {
						formattedBalance = Number(response.data.data.ta_total_amount)
					}
				}
				
				console.log(`SPL 토큰 보유수량 : ${formattedBalance}`)
				return formattedBalance;
			}
			
			return 0;

		} catch (error) {
			console.error('서버에서 잔액 조회 중 오류 발생:', error);
			alert('An issue occurred while fetching the token balance from the server')
			return 0;
		}
	}


	/**
	 * 코인 서버 잔액 조회
	 * @param walletAddress 
	 * @returns 
	 */
	const getBalanceListFromServer = async (walletAddress: string): Promise<any> => {
		// 파라미터 유효성 검사
		if (!walletAddress || typeof walletAddress !== 'string' || walletAddress.trim() === '') {
			console.error('유효하지 않은 지갑 주소입니다.');
			return null;
		}
		
		const network = await getBlockChain()
		const telegramId = await getTelegramId()

		try {
			const response = await axios.post(`${process.env.REACT_APP_QWALLET_SERVER_URL}/v1/tx/balancelist`, {
				wallet_address: walletAddress,
				network: network,
				coin_name: '',
				tid: telegramId
			});
			
			if (response.data.data && response.data.result === true) {
				console.log(`서버에서 받은 보유수량 리스트 :`, response.data.data)
				return response.data.data;
			}
			
			return null;
		} catch (error) {
			console.error('서버에서 보유수량 리스트 조회 중 오류 발생:', error);
			alert('An issue occurred while fetching the balance list from the server')
			return null;
		}

	}

	/**
	 * gas 확인
	 * @param walletAddress
	 */
	const estimateGas = async (walletAddress: string): Promise<string> => {
		try {
			if (!connection) {
				console.error('Connection is not initialized');
				return '0.0';
			}

			const currentTime = Date.now()
			const cachedData = gasPriceCache['gasPrice']

			if (cachedData && currentTime - cachedData.timestamp < gasPriceCacheExpiryTime) {
				console.log('Using cached gas price')
				return cachedData.gasFee
			}

			let gasFee = 0

			// 네트워크에서 가스 값 가져오기
			const walletPublicKey = new PublicKey(walletAddress);

			const recentBlockhash = await connection.getLatestBlockhash();

			const transaction = new Transaction({
				feePayer: walletPublicKey, // 트랜잭션의 주체
				recentBlockhash: recentBlockhash.blockhash, // 최신 블록 해시/ 여기에 트랜잭션에 포함할 명령어 추가 가능
			});
			
			// 기본 instruction 
			transaction.add(
				SystemProgram.transfer({
					fromPubkey: walletPublicKey,
					toPubkey: walletPublicKey, // 동일한 주소로 전송 (테스트 용도)
					lamports: 0, // 실제로 전송되지 않는 트랜잭션
				})
			);

			const message = transaction.compileMessage();
			const feeCalculator = await connection.getFeeForMessage(message);
	
			if (feeCalculator && feeCalculator.value) {
				gasFee = feeCalculator.value / 1_000_000_000; // Lamports를 SOL로 변환
			}

			
			gasPriceCache['gasPrice'] = {
				gasFee,
				timestamp: currentTime
			}
			setGasPriceCache(gasPriceCache)

			return gasFee.toString()
		} catch (error) {
			console.error('Error estimating gas fee:', error)
			alert('An issue occurred while fetching gas fee information')
			return '0.0'
		}
	}

	/**
	 * solana 코인/토큰 전송
	 * @param privateKey 
	 * @param toAddress 
	 * @param amount 
	 * @param tokenSymbol 
	 * @param getAssociatedTokenAddress 
	 * 
	 * @return signature
	 */
	const sendTransactionWithPrivateKey = 
	async (
		privateKey: string, 
		toAddress: string, 
		amount: string, 
		tokenSymbol: string, 
		tokenAddress: string = ''
	): Promise<string> => {
			if (Number(amount) < Number(process.env.REACT_APP_SOLANA_SEND_MIN_AMOUNT)) {
				console.error(`전송 최소 수량인 ${process.env.REACT_APP_SOLANA_SEND_MIN_AMOUNT} 이하 전송 : ${amount}`);
				const alertMessage = t('template_withdraw_screen_message_36') + ' ' + process.env.REACT_APP_SOLANA_SEND_MIN_AMOUNT;
				alert(alertMessage);
				return '';
			}

			try {
				const mainCurrency = solNetwork?.currency.toLowerCase() || 'sol'
				if(tokenSymbol === mainCurrency) {
					// sol 코인
					const signature = await sendSolCoin(privateKey, toAddress, +amount)
					return signature;
				} else {
					// spl 토큰
					const signature = await sendSplTokenV1(privateKey, toAddress, +amount, tokenAddress)
					return signature;
				}
			} catch (error) {
				console.log('Error solana sending transaction:', error)
				alert('An issue occurred while sending the transaction')
				throw error
			}
		}

		/**
	 * sol 코인 전송
	 * @param privateKey : 지갑 개인키
	 * @param toAddress 
	 * @param amount 
	 * 
	 * @return signature
	 */
	const sendSolCoin = 
	async (
		fromSecretKey:string, 
		toAddress:string, 
		amount:number
	): Promise<string> => {
		try {
			if (!connection) {
				console.error('Connection is not initialized');
				return '';
			}

			const fromSecretKeyUint8Array = bs58.decode(fromSecretKey);
			const fromKeypair = Keypair.fromSecretKey(fromSecretKeyUint8Array);

			const toPublicKey = new PublicKey(toAddress);
			const transaction = new Transaction().add(
				SystemProgram.transfer({
					fromPubkey: fromKeypair.publicKey,
					toPubkey: toPublicKey,
					lamports: amount * LAMPORTS_PER_SOL,
				})
			);

			const signature = await sendAndConfirmTransaction(connection, transaction, [fromKeypair]);
			return signature;
			
		} catch (error) {
			console.error('solana SOL 코인 전송 트랜잭션 중 오류 발생', error);
			alert('An issue occurred while sending the solana SOL coin transaction')
			return '';
		}
	}

	/**
	 * SPL 토큰 trasferCheck 전송 
	 * @param privateKey : 지갑 개인키
	 * @param toAddress : 수신자 주소
	 * @param amount : 전송할 토큰 수량
	 * @param mintAddress : spl 토큰 mintAddress
	 * 
	 * @return signature
	 */
	const sendSplTokenV1 = 
	async (
		fromSecretKey:string, 
		toAddress:string, 
		amount:number, 
		mintAddress:string
	): Promise<string> => {
		try {
			console.log('solana SPL Token 전송 시작:')

			if (!connection) {
				console.error('Connection is not initialized');
				return '';
			}

			const fromSecretKeyUint8Array = bs58.decode(fromSecretKey);
			const fromKeypair = Keypair.fromSecretKey(fromSecretKeyUint8Array);

			const toPublicKey = new PublicKey(toAddress);
			const mintPublicKey = new PublicKey(mintAddress);

			// 송신자와 수신자의 토큰 계정 주소
			const fromTokenAccount = await getAssociatedTokenAddress(mintPublicKey, fromKeypair.publicKey);
			const toTokenAccount = await getAssociatedTokenAddress(mintPublicKey, toPublicKey);

			// 수신자의 토큰 계정이 존재하지 않으면 생성
			const toTokenAccountInfo = await connection.getAccountInfo(toTokenAccount);

			const instructions = [];

			// 수수료 우선순위 설정 추가
			// microLamports는 1 SOL의 10^-6 단위입니다.
			// 예: microLamports가 1000000이면:
			// 1000000 * (1 SOL / 10^9) = 0.001 SOL의 우선순위 수수료
			// 
			// 전체 트랜잭션 수수료는:
			// 1. 기본 수수료 (약 0.0001 SOL)
			// 2. 우선순위 수수료 (microLamports * compute_units)
			// compute_units는 보통 200,000 정도입니다.
			//
			// 따라서 1000000 microLamports로 설정하면:
			// 0.0001 + (1000000 * 200000 / 10^9) = 0.0001 + 0.2 = 0.2001 SOL
			// 우선순위 수수료 설정
			// microLamports: 1000000 일 경우
			// 기본 수수료: ~0.0001 SOL
			// 우선순위 수수료: 1000000 * 200000 / 10^9 = 0.2 SOL
			// 총 예상 수수료: ~0.2001 SOL
			// 수수료가 낮으면 tx가 제한 시간 내에 처리되지 못해 실패함
			const priorityFee = ComputeBudgetProgram.setComputeUnitPrice({
				microLamports: 1000000
			});
			
			// 우선순위 수수료 명령어를 가장 앞에 추가
			instructions.push(priorityFee);

			if (!toTokenAccountInfo) {
				console.log('수신자의 토큰 계정이 없습니다. 새로 생성합니다.');
				instructions.push(
					createAssociatedTokenAccountInstruction(
					fromKeypair.publicKey, // 송신자의 공개키 (수수료 지불자)
					toTokenAccount,        // 생성할 토큰 계정
					toPublicKey,           // 수신자의 공개키
					mintPublicKey          // 민트 주소
					)
				);
			}

			// 토큰의 Decimals 값 조회
			const mintInfo = await getMint(connection, mintPublicKey);
			console.log('SPL Token mintInfo : ', mintInfo)

			if (!mintInfo || typeof mintInfo.decimals !== 'number') {
				console.error('mintInfo 정보가 null이거나 유효한 decimals 값을 포함하지 않습니다.');
				return '';
			}

			const decimals = mintInfo.decimals
			console.log('SPL Token decimals : ', decimals)

			// 전송할 수량을 lamports로 변환
			const amountInLamports = amount * Math.pow(10, decimals);
			console.log('SPL Token amountInLamports : ', amountInLamports)
			
			// 토큰 전송 명령 추가
			instructions.push(
				createTransferCheckedInstruction(
					fromTokenAccount, // 송신자의 토큰 계정
					mintPublicKey,    // 토큰민트 주소
					toTokenAccount,   // 수신자의 토큰 계정
					fromKeypair.publicKey, // 송신자의 공개키
					amountInLamports,  // 전송할 토큰 수량
					decimals // 토큰 decimals
				)
			);

			// Transaction 생성 시 최신 blockhash를 명시적으로 가져오기
			const latestBlockhash = await connection.getLatestBlockhash('confirmed');

			const transaction = new Transaction({
				feePayer: fromKeypair.publicKey,
				...latestBlockhash,
			}).add(...instructions);
			
			// 트랜잭션 설정 추가
			transaction.recentBlockhash = latestBlockhash.blockhash;
			transaction.lastValidBlockHeight = latestBlockhash.lastValidBlockHeight;
			console.log('SPL Token recentBlockhash:', latestBlockhash.blockhash, 'lastValidBlockHeight:', latestBlockhash.lastValidBlockHeight);

			const signature = await sendAndConfirmTransaction(connection, transaction, [fromKeypair]);

			return signature;
		} catch (error:any) {
			console.error('solana SPL 토큰 전송 트랜잭션 중 오류 발생:', error);
			alert('An issue occurred while sending the solana SPL token transaction')
			if (error.logs) {
				console.log('solana SPL 토큰 전송 트랜잭션 error.logs:', error.logs);
			}
			return '';
		}
	}

	/**
	 * 토큰 정보 조회
	 * @param contractAddress 
	 * @returns 
	 */
	const getTokenInfo = async (mintAddress: string) => {
		try {
			if (!connection) {
				console.error('Connection is not initialized');
				return null;
			}

			const mintPublicKey = new PublicKey(mintAddress);
			const mintInfo = await getMint(connection, mintPublicKey);
			console.log('1 getTokenInfo getMint 메소드에서 리턴되는 결과값:', mintInfo);

			const tokenName = '';
			const decimal = mintInfo.decimals;
			const symbol = '';

			const tokenInfo = {
				name: tokenName,
				symbol: symbol,
				decimals: decimal,
			};

			return tokenInfo
		} catch (error) {
			console.log('Error Solana getting token info:', error)
			return null
		}
	}

	// const getTokenInfo = async (mintAddress: string) => {
	// 	try {
	// 		const mintPublicKey: any = new PublicKey(mintAddress);

	// 		// const umi = createUmi(process.env.REACT_APP_RPC_URL!).use(mplTokenMetadata())
	// 		// const umi = createUmi(process.env.REACT_APP_RPC_URL!)
	// 		// const mint = generateSigner(umi)
	// 		// const asset = await fetchDigitalAsset(umi, mintPublicKey)
	// 		// console.log('1 getTokenInfo asset : ', asset)

	// 		// // initializing using the wallet adapter
	// 		//  const umi = createUmi("https://api.devnet.solana.com")
	// 		//    .use(walletAdapterIdentity(signer))
	// 		//    .use(mplTokenMetadata());

	// 		// const asset = await fetchDigitalAsset(umi, mintPublicKey)
	// 		// console.log('1 getTokenInfo asset : ', asset)

	// 	} catch (error) {
	// 		console.error('Error Solana getting token info:', error)
	// 		return null
	// 	}
	// }

	/**
	 * 토큰 상세정보 조회
	 * @param mintAddress 
	 * @returns 
	 */
	// const getTokenInfo = async (mintAddress: string) => {
	// 	try {
	// 		const mintPublicKey = new PublicKey(mintAddress);
	// 		const metadataPDA  = await getTokenMetadata(mintPublicKey);
	// 		const accountInfo = await connection.getAccountInfo(metadataPDA);

	// 		if (!accountInfo) {
	// 			throw new Error("Metadata account not found");
	// 		}

	// 		const metadata = await decodeMetadata(accountInfo.data);

	// 		const mintInfo = await getMint(connection, mintPublicKey);
	// 		const decimal = mintInfo.decimals;

	// 		const tokenDetailInfo = {
	// 			name: metadata.name,
	// 			symbol: metadata.symbol,
	// 			decimals: decimal,
	// 		};

	// 		return tokenDetailInfo;
	// 	} catch (error) {
	// 		console.log('Error Solana getting token detail info:', error)
	// 		return null
	// 	}
	// }

	/**
	 * 토큰 메타데이터 조회
	 * @param mintAddress 
	 * @returns 
	 */
	// const getTokenMetadata = async (mintAddress: any): Promise<PublicKey> => {

	// 	const [publicKey] = PublicKey.findProgramAddressSync(
	// 		[
	// 			Buffer.from('metadata'),
	// 			METADATA_PROGRAM_ID.toBuffer(),
	// 			mintAddress.toBuffer(),
	// 		],
	// 		METADATA_PROGRAM_ID
	// 	);
	// 	return publicKey;
	// }

	// Decode the metadata using Metaplex standards
	const decodeMetadata = async (data: any) => {
		const METADATA_PREFIX_LENGTH = 1; // Metaplex metadata는 고정된 접두사가 있습니다.
		const METADATA_START_INDEX = METADATA_PREFIX_LENGTH + 4; // 접두사 + U32 길이

		console.log('1 decodeMetadata data.toString : ', data.toString('utf8', METADATA_START_INDEX));

		const decoded = JSON.parse(data.toString('utf8', METADATA_START_INDEX, data.length));
		
		return {
			name: decoded.name,
			symbol: decoded.symbol,
			uri: decoded.uri,
		};
	}

	// Find Metadata PDA
	const getMetadataAccount = async (mintAddress: any) => {
		const metadataSeed = Buffer.from("metadata");
		const mintAddressBuffer = mintAddress.toBuffer();
		const [metadataPDA] = await PublicKey.findProgramAddress(
			[metadataSeed, METADATA_PROGRAM_ID.toBuffer(), mintAddressBuffer],
			METADATA_PROGRAM_ID
		);

		return metadataPDA;
	}
	








	/**
	 * 지정 지갑주소의 서명된 트랜잭션 txid 조회
	 * 
	 * @param walletAddress 
	 * @param limit 
	 * @returns 
	 */
	const getTxIdForAddress = 
	async (
		walletAddress: string, 
		limit: number=15
	): Promise<any> => {
		if (!connection) {
			console.error('Connection is not initialized');
			return [];
		}

		const publicKey = new PublicKey(walletAddress);

		let confirmedSignatures:any = []

		// 메인 지갑 tx 리스트
		const walletSignatures = await connection.getSignaturesForAddress(publicKey, {limit:limit});
		confirmedSignatures = [...walletSignatures]
		// 토큰 계정 지갑 tx 리스트
		const tokenAccounts = await connection.getTokenAccountsByOwner(publicKey, {programId: TOKEN_PROGRAM_ID,});
		for (const { pubkey: tokenAccountAddress } of tokenAccounts.value) {
			
			const tokenSignatures = await connection.getSignaturesForAddress(tokenAccountAddress, {limit:limit});
			confirmedSignatures = [...confirmedSignatures, ...tokenSignatures]
		}

		// txid 중복 제거
		const uniqueSignatures = new Map();
		confirmedSignatures.forEach((signatureInfo: { signature: string }) => {
			uniqueSignatures.set(signatureInfo.signature, signatureInfo);
		});

		confirmedSignatures = Array.from(uniqueSignatures.values());
		
		// blockNum 내림차순으로 
		const sortedSignatures = confirmedSignatures.sort((a: { slot: number }, b: { slot: number }) => b.slot - a.slot);
		const limitedSignatures = sortedSignatures.slice(0, limit);

		return limitedSignatures
	}

	/**
	 * 지정 지갑주소의 모든 서명된 트랜잭션 txid 조회
	 * @param walletAddress 
	 * @param order : 정렬순서 asc, desc
	 * @param maxLimit 
	 * @returns 
	*/
	const getAllTxIdForAddress = 
	async (
		walletAddress: string, 
		order: string = 'asc', 
		maxLimit: number = 1000
	): Promise<{ signature: string, slot: number }[]> => {
		try {
			if (!connection) {
				console.error('Connection is not initialized');
				return [];
			}

			const publicKey = new PublicKey(walletAddress);

			let txIdList: { signature: string, slot: number }[] = [];

			// 메인지갑 전체 시그니처 가져오기
			let signatures = await connection.getSignaturesForAddress(
				publicKey,
				{
					limit: maxLimit  // 최대 1000개씩 가져올 수 있음
				}
			);
			
			txIdList.push(...signatures.map(sig => ({ signature: sig.signature, slot: sig.slot })));
			console.log(`1 getAllTxIdForAddress 시그니처 개수: ${txIdList.length}`);

			// 메인 지갑의 전체 트랜잭션 리스트를 얻기 위한 페이징 처리
			let lastSignature = signatures[signatures.length - 1]?.signature;
			while (signatures.length === maxLimit) {  // 더 많은 트랜잭션이 있는 경우
				signatures = await connection.getSignaturesForAddress(
					publicKey,
					{
						before: lastSignature,
						limit: maxLimit
					}
				);

				if (signatures.length === 0) break;
				
				txIdList.push(...signatures.map(sig => ({ signature: sig.signature, slot: sig.slot })));
				console.log(`1-1 getAllTxIdForAddress 시그니처 개수: ${txIdList.length}`);

				lastSignature = signatures[signatures.length - 1]?.signature;
			}

			// 각 SPL 토큰 계정의 txid 리스트 가져오기
			const tokenAccounts = await connection.getTokenAccountsByOwner(publicKey, { programId: TOKEN_PROGRAM_ID });
			console.log(`3 getAllTxIdForAddress 토큰 계정 주소 리스트`, tokenAccounts);

			for (const { pubkey: tokenAccountAddress } of tokenAccounts.value) {
				// 토큰 계정의 시그니처 가져오기
				signatures = await connection.getSignaturesForAddress(
					tokenAccountAddress,
					{
						limit: maxLimit
					}
				);

				txIdList.push(...signatures.map(sig => ({ signature: sig.signature, slot: sig.slot })));
				console.log(`4 getAllTxIdForAddress 토큰 계정 시그니처 개수: ${signatures.length}`);

				// 페이징 처리하여 모든 시그니처 가져오기
				lastSignature = signatures[signatures.length - 1]?.signature;
				while (signatures.length === maxLimit) {
					const moreSignatures = await connection.getSignaturesForAddress(
						tokenAccountAddress,
						{
							before: lastSignature,
							limit: maxLimit
						}
					);

					if (moreSignatures.length === 0) break;
					
					txIdList.push(...moreSignatures.map(sig => ({ signature: sig.signature, slot: sig.slot })));
					console.log(`6 getAllTxIdForAddress 추가 시그니처 개수: ${moreSignatures.length}`);

					lastSignature = moreSignatures[moreSignatures.length - 1]?.signature;
				}
			}
			
			// 중복된 txid 제거
			const uniqueSignatures = new Map();
			txIdList.forEach((tx) => {
				uniqueSignatures.set(tx.signature, tx);
			});
			txIdList = Array.from(uniqueSignatures.values());
			console.log(`10 getAllTxIdForAddress 중복 제거 후 tx 수: ${txIdList.length}`);
			
			// 올림차순 정렬
			if('asc' === order) {
				txIdList.sort((a, b) => a.slot - b.slot);
			} else {
				txIdList.sort((a, b) => b.slot - a.slot);
			}
			console.log(`11 getAllTxIdForAddress txIdList : `, txIdList);

			return txIdList;
		} catch (error) {
			console.error('Error getting transaction list:', error);
			return [];
		}
	}

	/**
	* pre/postBalances로 실제 SOL 증감(delta)을 계산해,
	* + 이면 receive, - 이면 withdrawal 로 처리.
	* 
	* from / to 주소는 기존처럼
	*   (system && instruction.parsed?.type === 'transfer')
	* 인스트럭션을 찾아서 
	*   내 지갑이 sender or receiver인지 확인,
	*   매칭되면 거기서 'fromAddress', 'toAddress' 구함
	*/
	const getSolTxParseV2 = async (tx: any, walletAddress: string): Promise<any | null> => {
		if (!tx || !tx.transaction || !tx.meta) {
			return null;
		}

		const { slot, transaction, meta, blockTime } = tx;
		const fee = meta.fee || 0;
	  	const rentFee = 0; // rent fee 추가

		console.log(`1 getSolTxParse tx `, tx);

	  // 1) accountKeys에서 내 지갑 주소의 index 찾기
		const accountKeys = transaction.message.accountKeys || [];
		console.log(`2 getSolTxParse accountKeys `, accountKeys);

		let myIndex = -1;
		for (let i = 0; i < accountKeys.length; i++) {
			const key = accountKeys[i];
	    // key가 string인지, { pubkey: string } 객체인지에 따라 비교
			if (typeof key === 'string') {
				if (key === walletAddress) {
					myIndex = i;
					break;
				}
			} else if (key.pubkey === walletAddress) {
				myIndex = i;
				break;
			}
		}

		if (myIndex < 0) {
	    // 내 지갑 주소가 이 트랜잭션에 없으면 null
			console.log(`3 getSolTxParse 지갑주소가 TX에 없음 `, myIndex);
			return null;
		}

	 	 // 2) pre/postBalances의 차이 계산
		const preLamports = meta.preBalances[myIndex] ?? 0;
		const postLamports = meta.postBalances[myIndex] ?? 0;
		const delta = postLamports - preLamports;

		console.log(`4 getSolTxParse pre | post | delta ${preLamports} | ${postLamports} | ${delta}`);
		if (delta === 0) {
	    // 내 지갑의 SOL 변화 없음 → return null
			console.log(`5 getSolTxParse 지갑의 sol 변화 없음 `, delta);
			return null;
		}

	  	// + => receive, - => withdrawal
		let transferType: 'receive' | 'withdrawal';
		if (delta > 0) transferType = 'receive';
		else transferType = 'withdrawal';

		let absLamports = Math.abs(delta);
		if (delta > 0){
			absLamports = delta
		} 
		else {
			// 출금의 경우 fee 차감
			absLamports = absLamports - fee
		}

		const valueFloat = absLamports / LAMPORTS_PER_SOL;

	  	// 3) from / to 주소는 기존 로직처럼 "system + transfer" 인스트럭션 중 
	  	//  내 지갑이 sender/receiver인 첫 번째를 찾아 설정
		let fromAddress = '';
		let toAddress = '';

		const instructions = transaction.message.instructions.filter((instruction: any) => {
			return (
			instruction.program === 'system' &&
				instruction.parsed?.type === 'transfer'
			);
		});

		for (const ix of instructions) {
			const info = ix.parsed?.info || {};
			const { source, destination, lamports } = info;
			if (!source || !destination || !lamports) {
				continue;
			}

	  		// 만약 source가 내 지갑이면 → from=myWallet, to=destination
	  		// 만약 destination이 내 지갑이면 → from=source, to=myWallet
			if (source === walletAddress) {
				fromAddress = source;
				toAddress = destination;
		    break; // 첫 번째 발견만
			} else if (destination === walletAddress) {
				fromAddress = source;
				toAddress = destination;
		    break; // 첫 번째 발견만
			}
		}

	  // 4) 최종 결과
		return {
			slot,
			blockTime: blockTime ?? null,
			from: { hash: fromAddress },
			to: { hash: toAddress },
			// pre/postBalances의 차이
			amount: absLamports,
			value: valueFloat.toString(),
			fee: { value: fee },
			rentFee: { value: rentFee.toString() },
			timestamp: blockTime ?? null,
			transaction_hash: transaction.signatures[0] || '',
			mintAddress: '',
			transfer_type: transferType,
		};
	};

	/**
	 * spl 토큰 tx parse
	 * preBalance, postBalance 로 토큰 보유수량 체크 와 inner 인스트럭션
	 * 모두 확인해서 처리하게 변경
	 */
	const getSplTxParseV2 = async (tx: any, walletAddress: string) => {
		if (!tx || !tx.transaction || !tx.meta) {
			return null;
		}
	
		const { transaction, meta } = tx;
	
		console.log('getSplTxParseV2 transaction:', transaction);
		console.log('getSplTxParseV2 meta:', meta);

		// 1) pre/postTokenBalances 존재 여부 확인
		const hasTokenBalances =
		(meta.preTokenBalances && meta.preTokenBalances.length > 0) ||
		(meta.postTokenBalances && meta.postTokenBalances.length > 0);
	
		if (hasTokenBalances) {
			// --------------------------
			// (A) pre/postTokenBalances 방식
			// --------------------------
			return await parseByPrePostBalances(tx, walletAddress);
		} else {
			// --------------------------
			// (B) fallback: instruction-based 방식
			// --------------------------
			return await parseByInstructionFallback(tx, walletAddress);
		}
	}	
	
	/** 
	 * (A) pre/post 방식: 
	 * preTokenBalances / postTokenBalances 비교 -> 토큰 delta
	 * + from/to 는 optional instruction에서 유추 
	 */
	const parseByPrePostBalances = async (tx: any, walletAddress: string) => {
		const { slot, transaction, meta, blockTime } = tx;
		let fee = meta.fee || 0;
		let rentFee = 0;
	
		console.log(`1. parseByPrePostBalances: using balance approach`);
	
		// mintAddress: preTokenBalances[0] or postTokenBalances[0]
		const mintAddress =
			meta.preTokenBalances?.[0]?.mint ||
			meta.postTokenBalances?.[0]?.mint;
		console.log(`2. parseByPrePostBalances: mintAddress`, mintAddress);
	
		if (!mintAddress) {
			console.warn(`parseByPrePostBalances: No mint found`);
			return null;
		}
	
		const walletPubkey = new PublicKey(walletAddress);
		const mintPubkey = new PublicKey(mintAddress);
	
		// 모든 인스트럭션(top-level + inner)
		const topLevelInstrs = transaction.message.instructions || [];
		const innerInstrs = (meta.innerInstructions || []).flatMap(
			(obj: any) => obj.instructions || []
		);
		const allInstructions = [...topLevelInstrs, ...innerInstrs];
	
		// rentFee 계산: spl-associated-token-account create?
		const createIx = allInstructions.find((ix: any) => {
			return (
				ix.program === 'spl-associated-token-account' &&
				ix.parsed?.info?.source === walletAddress
			);
		});
		if (createIx) {
			// 내 지갑이 rentFee 지불자
			const payerIndex = findKeyIndex(transaction.message.accountKeys, walletAddress);
			if (payerIndex !== -1) {
				const preLamports = meta.preBalances[payerIndex] || 0;
				const postLamports = meta.postBalances[payerIndex] || 0;
				const lamportsDiff = preLamports - postLamports;
				rentFee = lamportsDiff - fee;
				if (rentFee < 0) rentFee = 0; // 안전 처리
			}
		}
	
		// 내 토큰 잔액 변화 찾기
		const myToken = findMyTokenBalanceChange(meta, walletAddress);
		if (!myToken) {
			// owner===walletAddress 인 토큰 계정이 pre/post에서 안 보이면 null
			return null;
		}
	
		let { delta, decimals, finalMintAddr, fromAddr, toAddr } = myToken;
	
		// delta>0 => receive, delta<0 => withdrawal
		const transferType = delta > 0 ? 'receive' : 'withdrawal';
		const absDelta = Math.abs(delta);
		const amountFloat = absDelta / Math.pow(10, decimals);
	
		// 1) 우선 my ATA 가져오기(표준 ATA)
		//    (주의: 실제로 트랜잭션에서 만든 임의 계정일 수 있으나,
		//     여기서는 표준 ATA 기준으로 인스트럭션 찾기)
		const myAtaPubkey = await getAssociatedTokenAddress(mintPubkey, walletPubkey);
	
		// 2) 인스트럭션에서 from/to 찾기 (ex. 'spl-token', type='transfer|transferChecked')
		let foundInstr = allInstructions.find((ix: any) => {
			if (ix.program !== 'spl-token') return false;
			const t = ix.parsed?.type;
			if (!['transfer', 'transferChecked'].includes(t)) return false;
	
			const info = ix.parsed?.info;
			// 만약 info.source===myAtaPubkey or info.destination===myAtaPubkey
			// 주의: 이건 '표준 ATA'만 인식. 
			return (
				info?.source === myAtaPubkey.toBase58() ||
				info?.destination === myAtaPubkey.toBase58()
			);
		});
	
		// 3) 인스트럭션에서 찾았다면, source/destination를 보고 fromAddr/toAddr 설정
		if (foundInstr) {
			const ixType = foundInstr.parsed.type;
			const info = foundInstr.parsed.info;
			let src = info.source;
			let dst = info.destination;
	
			// 만약 src===myAtaPubkey => from=walletAddress
			if (src === myAtaPubkey.toBase58()) {
				src = walletAddress;
			}
			if (dst === myAtaPubkey.toBase58()) {
				dst = walletAddress;
			}
	
			fromAddr = src;
			toAddr = dst;
		} else {
			// 만약 인스트럭션에서 못 찾았다면, 
			// delta>0 => to=wallet, delta<0 => from=wallet 로 기본값
			if (transferType === 'withdrawal') {
				fromAddr = walletAddress;
			} else {
				toAddr = walletAddress;
			}
		}
	
		console.log(`parseByPrePostBalances: final from=${fromAddr} to=${toAddr}`);
	
		return {
			slot,
			blockTime: blockTime ?? null,
			from: { hash: fromAddr },
			to: { hash: toAddr },
			amount: absDelta,
			value: amountFloat.toString(),
			fee: { value: meta.fee || 0 },
			rentFee: { value: rentFee.toString() },
			timestamp: blockTime,
			transaction_hash: transaction.signatures[0] || '',
			mintAddress: finalMintAddr,
			transfer_type: transferType,
		};
	}
	
	/** 
 * (B) fallback: instruction 기반 파싱 
 * (기존에 올리신 inner instruction까지 확인하는 코드)
 */
	const parseByInstructionFallback = async (tx: any, walletAddress: string) => {
		const { slot, transaction, meta, blockTime } = tx;
		let fee = meta.fee || 0;
		let rentFee = 0;
	
		console.log(`1. parseByInstructionFallback: using balance approach`);
	
		// mintAddress: preTokenBalances[0] or postTokenBalances[0]
		const mintAddress =
			meta.preTokenBalances?.[0]?.mint ||
			meta.postTokenBalances?.[0]?.mint;
		console.log(`2. parseByInstructionFallback: mintAddress`, mintAddress);
	
		if (!mintAddress) {
			console.warn(`parseByInstructionFallback: No mint found`);
			return null;
		}
	
		const walletPubkey = new PublicKey(walletAddress);
		const mintPubkey = new PublicKey(mintAddress);
	
		// 모든 인스트럭션(top-level + inner)
		const topLevelInstrs = transaction.message.instructions || [];
		const innerInstrs = (meta.innerInstructions || []).flatMap(
			(obj: any) => obj.instructions || []
		);
		const allInstructions = [...topLevelInstrs, ...innerInstrs];
	
		// rentFee 계산: spl-associated-token-account create?
		const createIx = allInstructions.find((ix: any) => {
			return (
				ix.program === 'spl-associated-token-account' &&
				ix.parsed?.info?.source === walletAddress
			);
		});
		if (createIx) {
			// 내 지갑이 rentFee 지불자
			const payerIndex = findKeyIndex(transaction.message.accountKeys, walletAddress);
			if (payerIndex !== -1) {
				const preLamports = meta.preBalances[payerIndex] || 0;
				const postLamports = meta.postBalances[payerIndex] || 0;
				const lamportsDiff = preLamports - postLamports;
				rentFee = lamportsDiff - fee;
				if (rentFee < 0) rentFee = 0; // 안전 처리
			}
		}
	
		// 내 토큰 잔액 변화 찾기
		const myToken = findMyTokenBalanceChange(meta, walletAddress);
		if (!myToken) {
			// owner===walletAddress 인 토큰 계정이 pre/post에서 안 보이면 null
			return null;
		}
	
		let { delta, decimals, finalMintAddr, fromAddr, toAddr } = myToken;
		if (delta === 0) {
			return null;
		}
	
		// delta>0 => receive, delta<0 => withdrawal
		const transferType = delta > 0 ? 'receive' : 'withdrawal';
		const absDelta = Math.abs(delta);
		const amountFloat = absDelta / Math.pow(10, decimals);
	
		// 1) 우선 my ATA 가져오기(표준 ATA)
		//    (주의: 실제로 트랜잭션에서 만든 임의 계정일 수 있으나,
		//     여기서는 표준 ATA 기준으로 인스트럭션 찾기)
		const myAtaPubkey = await getAssociatedTokenAddress(mintPubkey, walletPubkey);
	
		// 2) 인스트럭션에서 from/to 찾기 (ex. 'spl-token', type='transfer|transferChecked')
		let foundInstr = allInstructions.find((ix: any) => {
			if (ix.program !== 'spl-token') return false;
			const t = ix.parsed?.type;
			if (!['transfer', 'transferChecked'].includes(t)) return false;
	
			const info = ix.parsed?.info;
			// 만약 info.source===myAtaPubkey or info.destination===myAtaPubkey
			// 주의: 이건 '표준 ATA'만 인식. 
			return (
				info?.source === myAtaPubkey.toBase58() ||
				info?.destination === myAtaPubkey.toBase58()
			);
		});
	
		// 3) 인스트럭션에서 찾았다면, source/destination를 보고 fromAddr/toAddr 설정
		if (foundInstr) {
			const ixType = foundInstr.parsed.type;
			const info = foundInstr.parsed.info;
			let src = info.source;
			let dst = info.destination;
	
			// 만약 src===myAtaPubkey => from=walletAddress
			if (src === myAtaPubkey.toBase58()) {
				src = walletAddress;
			}
			if (dst === myAtaPubkey.toBase58()) {
				dst = walletAddress;
			}
	
			fromAddr = src;
			toAddr = dst;
		} else {
			// 만약 인스트럭션에서 못 찾았다면, 
			// delta>0 => to=wallet, delta<0 => from=wallet 로 기본값
			if (transferType === 'withdrawal') {
				fromAddr = walletAddress;
			} else {
				toAddr = walletAddress;
			}
		}
	
		console.log(`parseByPrePostBalances: final from=${fromAddr} to=${toAddr}`);
	
		return {
			slot,
			blockTime: blockTime ?? null,
			from: { hash: fromAddr },
			to: { hash: toAddr },
			amount: absDelta,
			value: amountFloat.toString(),
			fee: { value: meta.fee || 0 },
			rentFee: { value: rentFee.toString() },
			timestamp: blockTime,
			transaction_hash: transaction.signatures[0] || '',
			mintAddress: finalMintAddr,
			transfer_type: transferType,
		};
	}
	
	/** 
	* findKeyIndex: accountKeys 에서 지갑주소와 일치하는 index 검색
	*/
	function findKeyIndex(accountKeys: any[], walletAddress: string): number {
		for (let i = 0; i < accountKeys.length; i++) {
			const key = accountKeys[i];
			if (typeof key === 'string') {
				if (key === walletAddress) return i;
			} else if (key.pubkey === walletAddress) {
				return i;
			}
		}

		return -1;
	}

	/**
	* findMyTokenBalanceChange:
	*  1) preTokenBalances & postTokenBalances 중 "owner===walletAddress"
	*     를 찾아서 pre->post delta
	*  2) decimals, fromAddr, toAddr (simple example)
	*/
	function findMyTokenBalanceChange(meta: any, walletAddress: string) {
	  // e.g. pre/post 중 owner===walletAddress => delta
	  // 실제로 "owner" might be wallet or might be an ATA of the wallet 
	  // => depends on how Solana returns it.
	  // For simplicity, we'll assume "owner===walletAddress" means it's my account
	  // We'll just do the [0] index. If multiple token accounts, might do a loop
		const pre = meta.preTokenBalances?.find((tb: any) => tb.owner === walletAddress);
		const post = meta.postTokenBalances?.find((tb: any) => tb.owner === walletAddress);

		if (!pre && !post) {
			return null;
		}
		const decimals = pre?.uiTokenAmount?.decimals ?? post?.uiTokenAmount?.decimals ?? 0;
		const preAmount = Number(pre?.uiTokenAmount?.amount ?? '0');
		const postAmount = Number(post?.uiTokenAmount?.amount ?? '0');
		const delta = postAmount - preAmount;

		const finalMint = post?.mint || pre?.mint || '';

	  // fromAddr / toAddr? 
	  // In a truly robust approach, we'd parse instructions to see actual source/dest accounts 
	  // but here's a naive example:
		let fromAddr = (delta < 0) ? walletAddress : '';
		let toAddr   = (delta > 0) ? walletAddress : '';

		// delta 가 0 이면 내 지갑으로 보낸 것으로 판단
		if (delta === 0) {
			fromAddr = walletAddress;
			toAddr = walletAddress;
		}

		return {
			delta, decimals, finalMintAddr: finalMint,
			fromAddr, toAddr
		};
	}





	/**
	 * RPC 서버 트랜잭션 조회 요청
	 * @param signature 
	 * @param retries 
	 * @returns 
	 */
	const fetchTransactionRpc = async (signature: string, waitTime=700, retries = 3) => {

		if (!connection) {
			console.error('Connection is not initialized');
			return null;
		}

		const transaction = await connection.getTransaction(signature, { maxSupportedTransactionVersion: 0 });
		//const parsedTransaction = await connection.getParsedTransaction(signature, { maxSupportedTransactionVersion: 0 });

		console.log(`---------------------------1 fetchTransactionRpc getTransaction : `, transaction)
		//console.log(`---------------------------2 fetchTransactionRpc parsedTransaction : `, parsedTransaction)

		await sleep(waitTime);
		return transaction;
	}

	const fetchParsedTransactionRpc = async (signature: string, waitTime=700) => {
		if (!connection) {
			console.error('Connection is not initialized');
			return null;
		}

		// const transaction = await connection.getTransaction(signature, { maxSupportedTransactionVersion: 0 });
		const transaction = await connection.getParsedTransaction(signature, { maxSupportedTransactionVersion: 0 });
		await sleep(waitTime);

		const txString = JSON.stringify(transaction); // ParsedTransactionWithMeta 객체를 JSON 문자열로 변환
		const parsedTransaction = JSON.parse(txString); // JSON 문자열을 일반 객체로 변환

		// console.log(`---------------------------1 fetchTransactionRpc getTransaction : `, transaction)
		console.log(`---------------------------1 fetchParsedTransactionRpc parsedTransaction : `, transaction)
				
		return parsedTransaction;
	}

	/**
	 * 웹어셈블리 트랜잭션 조회 요청
	 * @param signature 
	 * @param retries 
	 * @returns 
	 */
	const fetchTransactionWasm = async (signature: string, waitTime=700) => {
		try {
      		// WASM 함수 호출
			const rpcHttpUrl = solNetwork?.rpc_http || ''
			const transaction = await fetch_transaction_data(signature, rpcHttpUrl);
			await sleep(waitTime);

      		// result은 JSON 문자열이므로 파싱
			const parsedData = await JSON.parse(transaction);
			const result = parsedData.result;

			return result;
		} catch (error) {
			console.error('Error 웹어셈블리 fetching transaction:', error);
		}

		return null;
	};

	/**
	 * 전체 트랜잭션 수 조회
	 * @param walletAddress 
	 * @returns 
	*/
	const getTotalTxCount = async (walletAddress: string, maxLimit:number = 1000) => {
		try {
			if (!connection) {
				console.error('Connection is not initialized');
				return 0;
			}

			const publicKey = new PublicKey(walletAddress);
			const startTime = new Date().getTime(); // 시작 시간 timestamp 저장

			let totalCount = 0

			// 메인지갑 전체 시그니처 수 가져오기
			const signatures = await connection.getSignaturesForAddress(
				publicKey,
				{
					limit: maxLimit  // 최대 1000개씩 가져올 수 있음
				}
			);
			
			totalCount = totalCount + signatures.length
			console.log(`1 getTotalTxCount totalCount 1`, totalCount)

			// 메인 지갑의 전체 트랜잭션 수를 얻기 위해 페이징 처리
			let lastSignature = signatures[signatures.length - 1]?.signature;
			while (signatures.length === maxLimit) {  // 더 많은 트랜잭션이 있는 경우
				const moreSignatures = await connection.getSignaturesForAddress(
					publicKey,
					{
						before: lastSignature,
						limit: maxLimit
					}
				);
	
				if (moreSignatures.length === 0) break;
				
				totalCount = totalCount + moreSignatures.length
				// console.log(`1-1 getTotalTxCount totalCount 1-1`, totalCount)

				lastSignature = moreSignatures[moreSignatures.length - 1]?.signature;
			}
			// console.log(`2 getTotalTxCount totalCount 2`, totalCount)

			// 각 spl 토큰계정의 txid 리스트
			const tokenAccounts = await connection.getTokenAccountsByOwner(publicKey, {programId: TOKEN_PROGRAM_ID,});
			// console.log(`3 getTotalTxCount 토큰 계정주소 리스트`, tokenAccounts)

			// spl 토큰 계정 txid 리스트
			for (const { pubkey: tokenAccountAddress } of tokenAccounts.value) {
				
				// 메인지갑 전체 시그니처 수 가져오기
				const signatures = await connection.getSignaturesForAddress(
					tokenAccountAddress,
					{
						limit: maxLimit  // 최대 1000개씩 가져올 수 있음
					}
				);
				// console.log(`4 getTotalTxCount 토큰 계정주소 signatures 수`, signatures.length)

				totalCount = totalCount + signatures.length
				// console.log(`5 getTotalTxCount totalCount 수 3`, totalCount)

				// 전체 트랜잭션 수를 얻기 위해 페이징 처리
				let lastSignature = signatures[signatures.length - 1]?.signature;
				while (signatures.length === maxLimit) {  // 더 많은 트랜잭션이 있는 경우
					const moreSignatures = await connection.getSignaturesForAddress(
						tokenAccountAddress,
						{
							before: lastSignature,
							limit: maxLimit
						}
					);
		
					if (moreSignatures.length === 0) break;
					
					totalCount = totalCount + moreSignatures.length
					console.log(`6 getTotalTxCount moreSignatures[moreSignatures.length 수`, moreSignatures.length)

					lastSignature = moreSignatures[moreSignatures.length - 1]?.signature;
				}
				// console.log(`7 getTotalTxCount 토큰 계정주소 signatures 수`, signatures.length)
			}
			
			console.log(`8 getTotalTransactionCount 메인지갑 주소 + 토큰 계정주소 전체 tx 수`, totalCount)
			
			const endTime = new Date().getTime(); // 시작 시간 timestamp 저장
			const elapsedTime = endTime - startTime;
			console.log(`9 getTotalTxCount ${elapsedTime} ms.`);

			return totalCount;
		} catch (error) {
			console.error('Error getting transaction count:', error);
			return 0;
		}
	}


	/**
	 * 메인지갑 트랜잭션 수 조회
	 * @param walletAddress 
	 * @returns 
	 */
	// const getLimitTxCount = async (walletAddress: string, maxLimit:number = 1000) => {
	// 	try {
	// 		const publicKey = new PublicKey(walletAddress);

	// 		let totalCount = 0

	// 		// 메인지갑 전체 시그니처 수 가져오기
	// 		const mainSignatures = await connection.getSignaturesForAddress(
	// 			publicKey,
	// 			{
	// 				limit: maxLimit  // 최대 1000개씩 가져올 수 있음
	// 			}
	// 		);

	// 		// 각 spl 토큰계정의 txid 리스트
	// 		const tokenAccounts = await connection.getTokenAccountsByOwner(publicKey, {programId: TOKEN_PROGRAM_ID,});
	// 		// console.log(`3 getTotalTxCount 토큰 계정주소 리스트`, tokenAccounts)

	// 		// 각 토큰 계정의 txid 리스트 가져오기
	// 		const tokenSignaturesPromises = tokenAccounts.value.map(({ pubkey: tokenAccountAddress }) =>
	// 			connection.getSignaturesForAddress(
	// 				tokenAccountAddress,
	// 				{
	// 					limit: maxLimit  // 최대 1000개씩 가져올 수 있음
	// 				}
	// 			)
	// 		);

	// 		const tokenSignaturesArrays = await Promise.all(tokenSignaturesPromises);

	// 		// 중복된 txid를 제거한 고유한 txid 집합
	// 		const uniqueTxIds = new Set<string>();

	// 		// 메인 지갑의 txid 추가
	// 		for (const sig of mainSignatures) {
	// 			uniqueTxIds.add(sig.signature);
	// 		}

	// 		// 각 토큰 계정의 txid 추가
	// 		for (const tokenSignatures of tokenSignaturesArrays) {
	// 			for (const tsig of tokenSignatures) {
	// 				uniqueTxIds.add(tsig.signature);
	// 			}
	// 		}

	// 		// 중복을 제거한 txid의 총 개수로 업데이트
	// 		totalCount = uniqueTxIds.size;
	// 		console.log(`2 getLimitTxCount 메인지갑 주소 + 토큰 계정주소 전체 tx 수`, totalCount)

	// 		return totalCount;
	// 	} catch (error) {
	// 		console.error('Error getting transaction count:', error);
	// 		return 0;
	// 	}
	// }

	/**
	 * 트랜잭션 조회 중지 flag 설정
	 * @param isStop 
	 */
	const stopRequestTxData = useCallback((isStop: boolean = true) => {
    console.log(`______________ stopRequestTxData reqTxStop 1-1 `, reqTxStop)
		console.log(`______________ stopRequestTxData reqTxStopRef 1-1 `, reqTxStopRef.current)
		setReqTxStop(true)
		console.log(`______________ stopRequestTxData reqTxStop 1-2 `, reqTxStop)
		console.log(`______________ stopRequestTxData reqTxStopRef 1-2 `, reqTxStopRef.current)
	}, []);
	

	/**
	 * 백그라운드 DB 현행화
	 * @param walletAddress 
	 * @param limit 
	 * @returns 
	 */
	const syncAllTransaction = async (walletAddress: string) => {
		if (true === runAllTransactionHistoryRef.current) {
			console.warn('이미 syncAllTransactionHistory 실행 중입니다.');
			return true;
		}

		const selNetwork = await getBlockChain()
		const telegramId = await getTelegramId()

		if (!selNetwork) {
			console.error('Error: network 설정값 없음.');
			return false; // 또는 적절한 에러 처리를 추가할 수 있습니다.
		}
		if (!telegramId) {
			console.error('Error: telegramId 설정값 없음.');
			return false; // 또는 적절한 에러 처리를 추가할 수 있습니다.
		}

		if (!connection) {
			console.error('Connection is not initialized');
			return false;
		}

		try {
			// 동작중 flag 변경
			setIsRunAllTransactionHistory(true)

			// stop state reset
			setReqTxStop(false)

			// DB TX 동기화 정보 표시 초기화
			setCountSyncProcess(0) // 진행한 갯수
			setCountSyncComplete(0) // 전체 갯수
			setLastSyncTxs([]) // 동기화 TX 전송내역

			setSyncStatus(SYNC_STATUS.CHECKING)

			// 동기화 키 생성
			const randomNumbers = Array.from({ length: 6 }, () => Math.floor(Math.random() * 10));
			const syncKey = `sync${Date.now()}${randomNumbers.join('')}`;
			setSyncKey(syncKey);
			
			// RPC 에서 모든 TX ID를 가져온다
			const rpcTxIds = await getAllTxIdForAddress(walletAddress, 'asc');
			console.log(`1 syncAllTransactionHistory rpcTxIds : `, rpcTxIds);

			if (!rpcTxIds || rpcTxIds.length === 0) {
				console.log('rpcTxIds 데이터가 없습니다. syncAllTransactionHistory 함수를 종료합니다.');
				// 동작중 flag 변경
				setSyncStatus(SYNC_STATUS.COMPLETE)
				return true;
			}
			// RPC 에서 보유한 코인 balance 정보를 가져온다
			const rpcCoinBalance = await getBalance(walletAddress, false);
			console.log(`2 syncAllTransactionHistory rpc coinBalance : `, rpcCoinBalance);

			// 지갑 주소의 모든 토큰 계정과 각 토큰 계정의 잔액 정보를 가져오기
			// 토큰 계정 리스트
			const tokenAccounts = await connection.getTokenAccountsByOwner(
				new PublicKey(walletAddress),
				{ programId: TOKEN_PROGRAM_ID }
			);

			const tokenBalances: { mintAddress: string; balance: string }[] = [];
			// 토큰 계정의 토큰 보유수량
			for (const { pubkey } of tokenAccounts.value) {
				if (reqTxStopRef.current) {
					setSyncStatus(SYNC_STATUS.STOP)
					return true;
				}

				const tokenAccount = await getAccount(connection, pubkey);
				console.log(`3-1 syncAllTransactionHistory tokenAccount : `, tokenAccount);

				const mintAddress = tokenAccount.mint.toBase58();
				console.log(`3-2 syncAllTransactionHistory mintAddress : `, mintAddress);
				const balance = await getTokenBalance(walletAddress, mintAddress, false);
				
				tokenBalances.push({ mintAddress, balance });
				console.log(`3-3 Mint Address: ${mintAddress}, 잔액: ${balance}`);
			}
			console.log(`3 syncAllTransactionHistory rpc tokenBalances : `, tokenBalances);

			// 서버에서 최신 tx id 데이터 10개를 가져온다
			const serverTxIds = await getServerTxIdList(walletAddress, selNetwork, telegramId, 1, 10);
			console.log(`4 syncAllTransactionHistory serverTxIds : `, serverTxIds);

			// 서버에서 코인 보유수량 리스트를 가져온다
			const mainCurrency = solNetwork?.currency.toLowerCase() || 'sol'
			const serverBalances = await getBalanceFromServer(walletAddress, mainCurrency, false);
			console.log(`5 syncAllTransactionHistory serverBalances : `, serverBalances);

			// 서버에서 토큰 보유수량 리스트를 가져온다
			const serverTokenBalances = await getBalanceListFromServer(walletAddress)
			console.log(`6 syncAllTransactionHistory serverTokenBalances : `, serverTokenBalances);

			// rpc - 서버 코인 수량 비교
			let rpcCoinMatch = false;
			if (rpcCoinBalance.toString() === serverBalances.toString()) {
				console.log('RPC와 서버의 코인 잔액이 일치합니다.');
				rpcCoinMatch = true;
			} else {
				console.warn(`RPC와 서버의 코인 잔액이 일치하지 않습니다. RPC: ${rpcCoinBalance}, 서버: ${serverBalances}`);
			}

    		// rpc - 서버 토큰 비교
			const matchingTokens = tokenBalances.filter(token =>
				serverTokenBalances.some((serverToken: any) => serverToken.ta_mintaddress === token.mintAddress)
			)
			console.log('7 syncAllTransactionHistory 지갑서버 토큰 정보:', matchingTokens)
			console.log('7 syncAllTransactionHistory 일치하는 토큰 정보:', matchingTokens)

			// rpc의 최신 tx id 1개와 서버에서 가져온 최신 tx id 1개가 일치하는지 확인한다
			let rpcTxIdMatch = false;
			if (rpcTxIds != null && serverTxIds != null && rpcTxIds.length !== 0 && serverTxIds.length !== 0) {
				const rpcLatestTxId = rpcTxIds[rpcTxIds.length - 1];
				const serverLatestTxId = serverTxIds[0].transaction_hash;	
				
				if (rpcLatestTxId.signature.toString() === serverLatestTxId.toString()) {
					console.log('RPC의 최신 tx id와 서버의 최신 tx id가 일치합니다.');
					rpcTxIdMatch = true;
				}
			}

			let rpcTokensMatch = true;
			if (!serverTokenBalances || serverTokenBalances.length === 0) {
				console.warn('서버의 토큰 보유수량 데이터가 없습니다.');
				rpcTokensMatch = false;
			} else if(matchingTokens.length > 0) {
				for (const token of matchingTokens) {
					const serverToken = serverTokenBalances.find((st: any) => st?.ta_mintaddress?.toString() === token.mintAddress.toString());
					
					console.log(`rpc 토큰 정보 :`, token);
					console.log(`서버 토큰 정보 :`, serverToken);

					console.log(`RPC - 서버 토큰 보유수량 비교 ${serverToken.ta_total_amount} | ${token.balance}`);
					if (!serverToken || token.balance.toString() !== serverToken.ta_total_amount.toString()) {
						console.warn(`토큰 ${token.mintAddress}의 잔액 ${token.balance} - ${serverToken.ta_total_amount} 이 일치하지 않습니다.`);
						rpcTokensMatch = false;
						break;
					}
				}
			} else {
				rpcTokensMatch = false;
			}

			if (rpcTxIdMatch && rpcCoinMatch && rpcTokensMatch) {
				console.log('RPC의 최신 tx id와 서버의 최신 tx id가 일치하며 코인, 토큰 잔액도 모두 일치합니다.');
				setSyncStatus(SYNC_STATUS.COMPLETE)
				return true;
			}

			// 일치하지 않으면 RPC 에서 가져온 tx id 리스트 중에 서버에서 가져온 최신 tx id가 포함되어 있는지 확인한다
			let newTxIds: any[] = [];

			if (!serverTxIds || serverTxIds.length === 0) {
				if (!rpcTxIds || rpcTxIds.length === 0) {
					console.log('RPC의 tx id 정보가 없음.');
					setSyncStatus(SYNC_STATUS.COMPLETE)
					return true;
				}
				newTxIds = rpcTxIds;
				console.log('serverTxIds가 없으므로 rpcTxIds를 그대로 사용.');
			} else {
				const serverLatestTxId = serverTxIds[0].transaction_hash;
				console.log(`serverLatestTxId : `, serverLatestTxId)
				const isIncluded = rpcTxIds.some((rpcTx: any) => rpcTx.signature === serverLatestTxId);
				
				if (isIncluded) {
					console.log('RPC의 tx id 리스트에 서버의 최신 tx id가 포함되어 있습니다.');
						
					// RPC tx id 최신 ~ 서버 tx id 최신 사이의 tx id를 추출한다
					const startIndex = rpcTxIds.findIndex((rpcTx: any) => rpcTx.signature.toString() === serverLatestTxId.toString());
					if (startIndex !== -1 && startIndex < rpcTxIds.length - 1) {
						// 서버 txid 위치 다음 ~ rpc 최신 txid 사이
						newTxIds = rpcTxIds.slice(startIndex + 1);
						console.log('추출된 새로운 tx id 리스트 1 :', newTxIds);
					}
				} else {
					console.log('RPC의 tx id 리스트에 서버의 최신 tx id가 포함되어 있지 않습니다.');
					// RPC에서 1000개의 tx id를 가져온다
					const additionalRpcTxIds = await getTxIdForAddress(walletAddress, 1000);
					const isIncludedInAdditional = additionalRpcTxIds.some((tx: any) => tx.signature === serverLatestTxId);
			
					if (isIncludedInAdditional) {
						console.log('RPC에서 추가로 가져온 1000개 tx id 리스트에 서버의 최신 tx id가 포함되어 있습니다.');
						const startIndex = additionalRpcTxIds.findIndex((tx: any) => tx.signature === serverLatestTxId);
						
						if (startIndex !== -1) {
							// newTxIds = additionalRpcTxIds.slice(0, startIndex);
							// 서버 txid 위치 ~ rpc 최신 txid 사이
							newTxIds = rpcTxIds.slice(startIndex + 1);
							console.log('추출된 새로운 tx id 리스트 2 :', newTxIds);
						} else {
							console.warn('추출할 tx id 리스트에서 서버의 최신 tx id를 찾을 수 없습니다.');
							setSyncStatus(SYNC_STATUS.ERROR)
							return false;
						}
					} else {
						console.log('RPC에서 추가로 가져온 1000개 tx id 리스트에 서버의 최신 tx id가 포함되어 있지 않습니다.');
						setSyncStatus(SYNC_STATUS.ERROR)
						return false;
					}
				}
			}

			console.log('8 syncAllTransactionHistory 트랜잭션 상세 요청할 newTxIds:', newTxIds)

			// newTxIds 수량이 0개 인 경우
			if(0 === newTxIds.length) {
				console.log('12 syncAllTransactionHistory DB 현행화 할 tx 없음')
				setSyncStatus(SYNC_STATUS.COMPLETE)
				return true;
			} 
			else if(0 < newTxIds.length && 10 >= newTxIds.length) {
				console.log('12 syncAllTransactionHistory DB 현행화 할 tx 데이터 10개 이하')
				await syncLimitTransaction(walletAddress, newTxIds, SYNC_STATUS.COMPLETE.toLowerCase())

				setSyncStatus(SYNC_STATUS.COMPLETE)
				return true;
			}
			else if(newTxIds.length > 10) {
				console.log('12 syncAllTransactionHistory DB 현행화 할 tx 데이터 10개 이상')
				const firstTxIds = newTxIds.slice(0, 10);
				newTxIds = newTxIds.slice(10); // 나머지 다시 저장

				const result = await syncLimitTransaction(walletAddress, firstTxIds, SYNC_STATUS.PROCESSING.toLowerCase())
				if(false === result) {
					console.log('12-1 syncAllTransactionHistory syncLimitTransaction 실행 오류')
					setSyncStatus(SYNC_STATUS.STOP)
					return false;
				}

				console.log(`12 syncAllTransactionHistory syncLimitTransaction result : `, result)
			}

			// DB 순차 현행화 수량
			setCountSyncComplete(newTxIds.length)

			// Start Generation Here
			// 추출한 tx_id 리스트의 트랜잭션 상세 정보를 순차적으로 가져와 서버에 추가한다
			let txParse = null
			let parsedTransactions:any = []
			let countProcess = 0;
			let dbTxSyncStatus = SYNC_STATUS.PROCESSING.toLowerCase();
			for (const signatureInfo of newTxIds) {
				setSyncStatus(SYNC_STATUS.PROCESSING)

				// const tx = await fetchTransactionWasm(signatureInfo.signature, reqDelay); // wasm
				const tx = await fetchParsedTransactionRpc(signatureInfo.signature, reqDelay) // rpc

				if (reqTxStopRef.current) {
					console.log(`############### syncAllTransaction reqTxStop 1-1`, reqTxStopRef.current)
					setSyncStatus(SYNC_STATUS.STOP)
					return true;
				}

				if (!tx || !tx.transaction?.message) {
					console.log(`8-1. syncAllTransactionHistory tx or tx.transaction.message is null`)
					// continue
					setSyncStatus(SYNC_STATUS.ERROR)
					return false;
				}
				console.log(`8-2 syncAllTransactionHistory tx : `, tx)
				console.log(`8-3 syncAllTransactionHistory tx transaction : `, tx.transaction)

				// sol 코인 전송 트랜잭션 종류 확인
				const isSolTransfer = tx.transaction.message.instructions.some((instruction: any) => {
					return instruction.program === "system" && instruction.parsed?.type === "transfer";
				});
				console.log(`8-4 syncAllTransactionHistory isSolTransfer : `, isSolTransfer)
				
				// newTxIds의 마지막 인덱스 도달 여부 확인
				const isLastIndex = countProcess === (newTxIds.length-1);
				dbTxSyncStatus = SYNC_STATUS.PROCESSING.toLowerCase();
				if (isLastIndex) {
					dbTxSyncStatus = SYNC_STATUS.COMPLETE.toLowerCase();
					console.log(`8-5 syncAllTransactionHistory 마지막 값 도달 DB TX 현행화 상태 설정 : `, dbTxSyncStatus)
				}

				if (isSolTransfer) {
					// SOL 트랜잭션 상세 정보
					txParse = await getSolTxParseV2(tx, walletAddress)
					console.log(`10-1 syncAllTransactionHistory SOL txParse `, txParse)

					if(txParse) {
						parsedTransactions.push(txParse)

						// tokenInfo 확인
						const tokenInfo = await defaultTokenContext.searchTokenContract(txParse.mintAddress)

						let symbol = solNetwork?.currency.toLowerCase() || 'sol'
						if(tokenInfo?.contract === txParse.mintAddress) {
							// 토큰 정보가 있는 경우
							symbol = tokenInfo?.symbol.toLowerCase() || ''
						} else if ('' !== txParse.mintAddress && tokenInfo?.contract !== txParse.mintAddress) {
							// spl 토큰이고 토큰 정보가 없는 경우
							symbol = txParse.mintAddress
						}

						// 서버에 트랜잭션 데이터 저장
						const response = await addServerTxHistory(
							walletAddress,
							selNetwork,
							telegramId,
							txParse.transaction_hash,
							txParse.slot,
							txParse.timestamp,
							symbol,
							txParse.mintAddress,
							txParse.from.hash,
							txParse.to.hash,
							txParse.amount.toString(),
							txParse.fee.value,
							txParse.rentFee.value,
							txParse.transfer_type,
							syncKeyRef.current,
							dbTxSyncStatus
						);
						console.log(`10-2 syncAllTransactionHistory addServerTxHistory response `, response)
						
						if(true === response.result && response.data.length > 0) {
							// DB 현행화 진행 수량
							countProcess = countProcess+1;
							setCountSyncProcess(countProcess)

							await updateLastTxTo(txParse, walletAddress, symbol, tokenInfo);
						} else if(false === response.result && 
							(323 === response.error_code || 324 === response.error_code || 325 === response.error_code)) {
							console.warn(`10-3 syncAllTransactionHistory addServerTxHistory fail `, response.message)
							setSyncStatus(SYNC_STATUS.SYNCKEY_ERROR)
							return true;
						} else if(false === response.result) {
							console.warn(`10-4 syncAllTransactionHistory addServerTxHistory fail `, response.message)
							setSyncStatus(SYNC_STATUS.STOP)
							return true;
						}

					} else {
						console.log(`10-2 syncAllTransactionHistory getSolTxParse txParse is null : ${signatureInfo.signature}`)
					}
				} 
				
				console.log(`11-0-0 syncAllTransactionHistory tx.transaction.message.instructions `, tx.transaction.message.instructions)
				let isSplTransfer = tx.transaction.message.instructions.some((instruction: any) => {
					return instruction.program === 'spl-token' && (instruction.parsed?.type === 'transfer' || instruction.parsed?.type === 'transferChecked');
				});

				// inner 인스트럭션도 확인
				const inner = tx.meta.innerInstructions || [];
				console.log(`11-0-1 syncAllTransactionHistory tx.meta.innerInstructions `, inner)
				for (const obj of inner) {
					const arr = obj.instructions || [];
					const match = arr.some((ix: any) => {
						return ix.program === 'spl-token' && (ix.parsed?.type === 'transfer' || ix.parsed?.type === 'transferChecked');
					});
					if (match) {
						isSplTransfer = true;
						break;
					}
				}

				console.log(`11-0-2 syncAllTransactionHistory isSplTransfer `, isSplTransfer)

				if(isSplTransfer) {
					txParse = await getSplTxParseV2(tx, walletAddress);
					console.log(`11-1 syncAllTransactionHistory SPL txParse `, txParse)

					if (txParse) {
						parsedTransactions.push(txParse);

						// tokenInfo 확인
						const tokenInfo = await defaultTokenContext.searchTokenContract(txParse.mintAddress)

						let symbol = solNetwork?.currency.toLowerCase() || 'sol'
						if(tokenInfo?.contract === txParse.mintAddress) {
							// 토큰 정보가 있는 경우
							symbol = tokenInfo?.symbol.toLowerCase() || ''
						} else if ('' !== txParse.mintAddress && tokenInfo?.contract !== txParse.mintAddress) {
							// spl 토큰이고 토큰 정보가 없는 경우
							symbol = txParse.mintAddress
						}

						// 서버에 트랜잭션 데이터 저장
						const response = await addServerTxHistory(
							walletAddress,
							selNetwork,
							telegramId,
							txParse.transaction_hash,
							txParse.slot,
							txParse.timestamp,
							symbol,
							txParse.mintAddress,
							txParse.from.hash,
							txParse.to.hash,
							txParse.amount.toString(),
							txParse.fee.value,
							txParse.rentFee.value,
							txParse.transfer_type,
							syncKeyRef.current,
							dbTxSyncStatus
						);
						console.log(`11-2 syncAllTransactionHistory addServerTxHistory response `, response)

						if(true === response.result && response.data.length > 0) {
							// DB 현행화 진행 수량
							countProcess = countProcess+1;
							setCountSyncProcess(countProcess)

							await updateLastTxTo(txParse, walletAddress, symbol, tokenInfo);
						} else if(false === response.result && 
							(323 == response.error_code || 324 == response.error_code || 325 == response.error_code)) {
							console.warn(`10-3 syncAllTransactionHistory addServerTxHistory fail `, response.message)
							setSyncStatus(SYNC_STATUS.SYNCKEY_ERROR)
							return true;
						} else if(false === response.result && 301 === response.error_code) {
							console.warn(`10-4 syncAllTransactionHistory addServerTxHistory overlap `, response.message)
						} else if(false === response.result) {
							console.warn(`10-4 syncAllTransactionHistory addServerTxHistory fail `, response.message)
							setSyncStatus(SYNC_STATUS.STOP)
							return true;
						}

					} else {
						console.log(`11-2 syncAllTransactionHistory getSplTxParsetxParse is null : ${signatureInfo.signature}`)
					}
				}
			}

			console.log('12 syncAllTransactionHistory DB 현행화 완료:')
			setSyncStatus(SYNC_STATUS.COMPLETE)

			return true;
			
		} catch(error) {
			console.error(`Error syncAllTransactionHistory : `, error)
			setSyncStatus(SYNC_STATUS.ERROR)
			setReqTxStop(false)
			return false;
		}
		finally {
			// 동작중 flag 초기화
			setIsRunAllTransactionHistory(false)
			setReqTxStop(false)

			console.log(`종료 syncAllTransactionHistory`)
		}
	}

	/**
	 * 10개 이하 인 경우 트랜잭션 데이터 동기화
	 * @param walletAddress 
	 * @param signatures 
	 * @returns 
	 */
	const syncLimitTransaction = 
	async (
		walletAddress: string, 
		signatures: any, 
		syncStatus: string
	): Promise<any> => {
		if (true === runLimitTransactionHistoryRef.current) {
			console.log('이미 syncLimitTransaction 실행 중입니다.');
			return {items: []}
		}
		
		try {
			// 동작중 flag 변경
			setIsRunLimitTransactionHistory(true)

			// tx 요청 중지 flag 초기화
			setReqTxStop(false)

			const selNetwork = await getBlockChain()
			const telegramId = await getTelegramId()
		
			if (!selNetwork) {
				console.error('Error: network 설정값 없음.');
				return false; // 또는 적절한 에러 처리를 추가할 수 있습니다.
			}
			if (!telegramId) {
				console.error('Error: telegramId 설정값 없음.');
				return false; // 또는 적절한 에러 처리를 추가할 수 있습니다.
			}

			console.log(`---------------syncLimitTransaction 시작`);

			// 메인지갑 + spl 토큰 지갑 tx 리스트 병합
			const confirmedSignatures = signatures

			//----------------------------------------------------------------------------------------------------------------------------------//
			// tx 데이터 비동기 요청
			const transactionPromises = confirmedSignatures.map((signatureInfo:any) => 
				// fetchParsedTransactionRpc(signatureInfo.signature, 10) // rpc
				fetchTransactionWasm(signatureInfo.signature, 10) // wasm
			);
			const transactions = await Promise.all(transactionPromises);
			//----------------------------------------------------------------------------------------------------------------------------------//

			//----------------------------------------------------------------------------------------------------------------------------------//
			// 웹어셈블리 사용할 경우 순차 요청
			// 웹어셈블리 메소드로 Promise.all을 사용할 때 발생하는 문제:
			// 1. 웹어셈블리는 단일 스레드에서 실행되므로 동시에 여러 요청을 처리하기 어려움
			// 2. 메모리 공유 문제로 인해 병렬 처리시 경쟁 상태(race condition)가 발생할 수 있음
			// 3. 웹어셈블리 인스턴스가 공유되어 동시 접근시 충돌이 발생할 수 있음
			
			// 해결방법:
			// 1. 순차적으로 처리하거나 
			// 2. 웹어셈블리 인스턴스를 요청마다 새로 생성하거나
			// 3. RPC 방식으로 전환
			// 순차적으로 트랜잭션 처리
			// let safeTransactions = [];
			// for (const signatureInfo of confirmedSignatures) {
			// 	try {
			// 		if(reqTxStop) {
			// 			console.log(`reqTxStop `, reqTxStop);
			// 			setReqTxStop(false);
			// 			break;
			// 		}
			// 		const transaction = await fetchTransactionWasm(signatureInfo.signature, 10);
			// 		if (transaction) {
			// 			safeTransactions.push(transaction);
			// 		}
			// 	} catch (error) {
			// 		console.error(`트랜잭션 처리 중 오류 발생: ${signatureInfo.signature}`, error);
			// 		continue;
			// 	}
			// }
			// null이 아닌 성공한 트랜잭션만 필터링
			// const transactions = safeTransactions.filter(tx => tx !== null);
			//----------------------------------------------------------------------------------------------------------------------------------//
			
			if (reqTxStopRef.current) {
				setSyncStatus(SYNC_STATUS.STOP)
				return true;
			}

			// tx 디테일 파싱
			const txDetailPromises = transactions.map(async (tx) => {
				if (tx && tx.transaction.message) {
					// console.log(`1 getAllTransactionHistory tx `, tx)
					
					if (reqTxStopRef.current) {
						setSyncStatus(SYNC_STATUS.STOP)
						return true;
					}

					// sol 코인 전송 트랜잭션 종류 확인
					const isSolTransfer = tx.transaction.message.instructions.some((instruction: any) => {
						return instruction.program === "system" && instruction.parsed?.type === "transfer";
					});

					if (isSolTransfer) {
						// SOL 트랜잭션 상세 정보
						const txParse = await getSolTxParseV2(tx, walletAddress);
						console.log(`8 syncLimitTransaction SOL txParse `, txParse);

						if (txParse) {
							return txParse;
						}
					} 
					
					// program 과 인스트럭션 타입 확인
					console.log(`9-1 syncLimitTransaction tx.transaction.message.instructions `, tx.transaction.message.instructions)
					let isSplTransfer = tx.transaction.message.instructions.some((instruction: any) => {
						return instruction.program === 'spl-token' && (instruction.parsed?.type === 'transfer' || instruction.parsed?.type === 'transferChecked');
					});
					console.log(`9-2 syncLimitTransaction isSplTransfer `, isSplTransfer)

					// inner 인스트럭션도 program 과 인스트럭션 타입 확인
					let isInnerSplTransfer = false;
					const inner = tx.meta.innerInstructions || [];
					console.log(`9-3 syncLimitTransaction tx.meta.innerInstructions `, inner)
					for (const obj of inner) {
						const arr = obj.instructions || [];
						const match = arr.some((ix: any) => {
							return ix.program === 'spl-token' && (ix.parsed?.type === 'transfer' || ix.parsed?.type === 'transferChecked');
						});
						if (match) {
							isInnerSplTransfer = true;
							break;
						}
					}
					console.log(`9-4 syncLimitTransaction isInnerSplTransfer `, isInnerSplTransfer)

					if(isSplTransfer || isInnerSplTransfer) {
						// SPL 토큰 트랜잭션 상세 정보
						const txParse = await getSplTxParseV2(tx, walletAddress);
						console.log(`9-5 syncLimitTransaction SPL txParse `, txParse);

						if (txParse) {
							return txParse;
						}
					}
				}
				return null;
			});

			// tx 상세정보 파싱
			const parsedTxDetails = (await Promise.all(txDetailPromises)).filter(tx => tx !== null);
			console.log(`10 syncLimitTransaction parsedTxDetails `, parsedTxDetails);
		
			// server tx 동기 저장 요청
			for (const txParse of parsedTxDetails) {
				if (typeof txParse === 'object' && txParse !== null) {
					const tokenInfo = await defaultTokenContext.searchTokenContract(txParse.mintAddress);
					let symbol = solNetwork?.currency.toLowerCase() || 'sol'
					
					if (tokenInfo?.contract === txParse.mintAddress) {
						symbol = tokenInfo?.symbol.toLowerCase() || '';
					} else if ('' !== txParse.mintAddress && tokenInfo?.contract !== txParse.mintAddress) {
						symbol = txParse.mintAddress;
					}
				
					const response = await addServerTxHistory(
						walletAddress,
						selNetwork,
						telegramId,
						txParse.transaction_hash,
						txParse.slot,
						txParse.timestamp,
						symbol,
						txParse.mintAddress,
						txParse.from.hash,
						txParse.to.hash,
						txParse.amount.toString(),
						txParse.fee.value,
						txParse.rentFee.value,
						txParse.transfer_type,
						syncKeyRef.current,
						syncStatus
					);

					console.log(`10-2 syncLimitTransaction addServerTxHistory response `, response)

					if(true === response.result && response.data.length > 0) {
						// DB 현행화 진행 수량
						await updateLastTxTo(txParse, walletAddress, symbol, tokenInfo);
					} else if(false === response.result && 
						(323 === response.error_code || 324 === response.error_code || 325 === response.error_code)) {
						console.warn(`10-3 syncLimitTransaction addServerTxHistory fail `, response.message)
						setSyncStatus(SYNC_STATUS.SYNCKEY_ERROR)
						return true;
					} else if(false === response.result && 301 === response.error_code) {
						// 중복된 txid
						console.warn(`10-4 syncLimitTransaction addServerTxHistory txid overlap `, response.message)
					} else if(false === response.result) {
						// 중복된 txid
						console.warn(`10-5 syncLimitTransaction addServerTxHistory fail `, response.message)
						setSyncStatus(SYNC_STATUS.STOP)
						return true;
					}
				}
			}

			return true;
		}
		catch (error) {
			console.error(`Error syncLimitTransaction`, error)
		} finally {
			// 동작중 flag 초기화
			setIsRunLimitTransactionHistory(false)
			setReqTxStop(false)

			console.log(`종료 syncLimitTransaction`)
		}

		return false;
	}


	/**
	 * server에 tx 데이터 추가 요청
	 * 
	 * @param walletAddress 
	 * @param network 
	 * @param tid 
	 * @param txId 
	 * @param blockNum 
	 * @param timeStamp 
	 * @param coinName 
	 * @param from 
	 * @param to 
	 * @param amount 
	 * @param fee 
	 * @param rentFee 
	 * @param transferType 
	 * @param syncKey 
	 * 
	 * @returns 
	 */
	const addServerTxHistory = async (
			walletAddress: string, 
			network: string, 
			tid: string, 
			txId:string, 
			blockNum:string, 
			timeStamp:string, 
			coinName:string, 
			mintAddress:string, 
			from:string, 
			to:string, 
			amount:string, 
			fee:string,
			rentFee:string,	
			transferType:string,
			syncKey:string,
			syncStatus:string
	): Promise<any | null> => {
		try {
			// WASM 함수 호출
			const response = await add_server_tx_history(
				process.env.REACT_APP_QWALLET_SERVER_URL!,
				walletAddress,
				network,
				tid,
				txId,
				blockNum.toString(),
				timeStamp.toString(),
				coinName,
				mintAddress,
				from,
				to,
				amount.toString(),
				fee.toString(),
				rentFee.toString(),
				transferType,
				syncKey,
				syncStatus
			);
			console.log(`response`, response)

			const result = JSON.parse(response)
			console.log(`JSON.parse result`, result)

			return result
		} catch (error) {
			console.error('qWallet 서버에서 tx 데이터 추가 중 오류 발생:', error);
			throw error;
		}
	}

	/**
	 * tx id 리스트 요청
	 * 
	 * @param walletAddress 
	 * @param network 
	 * @param tid 
	 * @param limit 
	 * @returns 
	 */
	const getServerTxIdList = async (
		walletAddress: string, 
		network: string, 
		tid: string, 
		page: number=1, 
		take: number=5
	): Promise<any | null> => {
		try {
			if (!walletAddress || !network || !tid) {
				console.error('유효하지 않은 파라미터: walletAddress, network, tid는 필수입니다.');
				return null;
			}

			const result = await get_server_tx_id_list(
				process.env.REACT_APP_QWALLET_SERVER_URL!,
				walletAddress,
				network,
				tid,
				page,
				take,
			);
			console.log(`getServerTxIdList result`, result)

			const response = JSON.parse(result)
			console.log(`JSON.parse response`, response)

			// key name 변경
			if(true === response.result && response.data.length > 0) {
				const transformedData = response.data.map((item: any) => ({
					slot: item.th_blocknum,
					transaction_hash: item.th_txid
				}));

				return transformedData
			} else {
				return null;
			}
		} catch (error) {
			console.error('qWallet 서버에서 tx id 리스트 요청 중 오류 발생:', error);
			throw error;
		}
	}

	/**
	 * tx 데이터를 전송내역 형태로 변경
	 * 
	 * @param tx 
	 * @param accountAddress 
	 * @param index 
	 * @param symbol 
	 * @param tokenInfo 
	 * 
	 * @returns 
	 */
	const updateLastTxTo = (tx: any, accountAddress: string, symbol: string, tokenInfo: any) => {
		const txId = tx.transaction_hash;
		const fromAddress = tx.from.hash;
		const detailDate = moment(tx.timestamp * 1000).format('YYYY. M. D');
		
		let amount = '0'
		if(null !== tokenInfo && !tx.mintAddress) {
			
			amount = (tx.amount / Math.pow(10, tokenInfo?.decimal)).toString();
		} else {
			amount = (tx.amount / LAMPORTS_PER_SOL).toString();
		}

		// symbol이 4글자 이상인 경우 앞 4글자만 표시
		symbol = symbol.substring(0, 4);
		
		const txData: SyncTx = {
			id: `${txId}`,
			type: fromAddress === accountAddress
				? i18next.t('template_wallet_screen_message_09') 
				: i18next.t('template_wallet_screen_message_10'),
			amount: amount,
			currency: symbol.toLocaleUpperCase(),
			date: detailDate,
			direction: fromAddress === accountAddress ? 'out' : 'in'
		}

		// 최근 3개 트랜잭션만 유지
		setLastSyncTxs(prev => {
			const updated = [txData, ...prev];
			if (updated.length > 3) {
				updated.pop();
			}
			return updated;
		});

		return txData;
	}

	

	/**
	 * qWallet 서버에서 마지막 3개 tx data 요청
	 * @param walletAddress : 지갑 주소
	 * @param network : 네트워크 종류
	 * @param tid : 텔레그럄 id
	 * @param tokenName : 토큰 이름
	 * @param page : 페이지 번호
	 * @param take : 페이지당 표시할 데이터 수
	 * 
	 * @returns jsonArray
	 */
	const getServerTxHistory = 
	async (
		walletAddress: string, 
		network: string, 
		tid: string, 
		tokenName: string, 
		page: number=1, 
		take: number=5
	): Promise<any | null> => {
		try {
			const result = await get_server_tx_history(
				process.env.REACT_APP_QWALLET_SERVER_URL!,
				walletAddress,
				network,
				tid,
				tokenName,
				page,
				take
			);
			
			console.log(`getServerTxHistory result`, result)

			const response = JSON.parse(result)
			console.log(`JSON.parse response`, response)

			// key name 변경
			if(true === response.result && response.data.length > 0) {
				
				console.log(`response.data`, response.data)

				const transformedData = response.data.map((item: any) => ({
					slot: Number(item.th_blocknum),
					blockTime: item.th_timestamp,
					from: {
						hash: item.th_from
					},
					to: {
						hash: item.th_to
					},
					amount: item.th_amount,
					value: (item.th_amount / Math.pow(10, item.tokenInfo?.ti_decimal)).toString(),
					fee: {
						value: (item.th_fee / LAMPORTS_PER_SOL).toString(),
					},
					rentFee: {
						value: item.th_rentfee
					},
					timestamp: item.th_timestamp,
					transaction_hash: item.th_txid,
					transfer_type: item.th_transfertype,
					coin_type: item.tokenInfo?.ti_type,
					coin_name: item.th_coinname
				}));

				return {items: transformedData}
			} else {
				return {items: []}
			}
		} catch (error) {
			console.error('qWallet 서버에서 tx 데이터 요청 중 오류 발생:', error);
			return {items: []}
		}
	}

	/**
	 * 블록 익스플로러 url
	 * @param chainName 
	 * @param address 
	 */
	const getBlockScanUrl = 
	async (
		txid: string
	): Promise<string> => {
		const blockExplorerUrl = solNetwork?.blockexp || '';
		return `${blockExplorerUrl}/tx/${txid}?cluster=mainnet-beta`;
	}

	/**
	 * address 유효성 검사
	 * @param address 
	 * @returns 
	 */
	const isValidAddress = async (address: string): Promise<boolean> => {
		try {
			if (address) {
				// solana 지갑 형식
				const publicKey = new PublicKey(address); // 유효하지 않은 주소일 경우 예외 발생
				return PublicKey.isOnCurve(publicKey.toBytes()); // PublicKey가 유효한 곡선 위에 있는지 확인
			}
			return false; // address가 제공되지 않거나 다른 네트워크일 경우 기본적으로 false 반환
		} catch (error) {
			console.error(`SolanaContext isValidAddress error : ${error}`);
			return false; // 예외가 발생하면 false 반환
		}
	};

	return <SolanaContext.Provider value={{ 
		balance,
		networkName,
		countSyncProcess,
		countSyncComplete,
		syncStatus,
		reqTxStop,
		lastSyncTxs,
		startDbSyncTimer,
		stopDbSyncTimer,
		getBalance,
		getBalanceFromServer,
		getTokenBalance,
		getTokenBalanceFromServer,
		getTotalTxCount,
		estimateGas,
		sendTransactionWithPrivateKey,
		stopRequestTxData,
		syncAllTransaction,
		getTokenInfo,
		getServerTxHistory,
		getBlockScanUrl,
		isValidAddress
	}}>{children}</SolanaContext.Provider>

}
